/**
 * API client. One place that knows about transport, so components deal in data.
 *
 * Authentication is a session COOKIE, set by `/auth/login`. Nothing here holds a
 * credential, and that is the point: the cookie is httpOnly, so this code cannot
 * read it even if it wanted to, and an XSS on this page cannot steal a session it
 * has no access to.
 *
 * That replaces the API token this used to keep in localStorage. A token in
 * localStorage is readable by every script the page ever loads, which makes any
 * third-party script a session-theft vector — and it also could not distinguish
 * one person from another, so the audit trail said "writer" for everyone.
 */

declare global {
  interface ImportMetaEnv {
    /** Where the API answers, when it is not the origin this bundle came from. */
    readonly VITE_API_BASE?: string
  }
  interface ImportMeta {
    readonly env: ImportMetaEnv
  }
}

/**
 * The API's address, decided when this bundle is built.
 *
 * Empty is the default and the whole reason this is safe: one process serves
 * this bundle AND answers `/api/...`, so a relative path is already correct,
 * and an unset variable leaves every request below byte-for-byte what it was.
 * Every gate, the dev server's proxy and `npm run dev` are that shape.
 *
 * Set it when the two are split — the UI on a CDN at `app.example.com`, the
 * API on its own host at `api.example.com` — because then a relative path is
 * not a request to the API at all, it is a request to the CDN, which has no
 * `/api/page` and will hand back the writer's own index.html with a 200. That
 * is the worst failure shape available: no error anywhere, just JSON.parse
 * refusing a document nobody asked for.
 *
 * Build time rather than run time: the browser has to know before it makes its
 * first request, and a request to ask where to make requests has the same
 * problem one turn earlier.
 */
const API_BASE = (import.meta.env.VITE_API_BASE ?? '').replace(/\/+$/, '')

/**
 * Every URL this client asks for, formed in one place.
 *
 * Exported because two things OUTSIDE a fetch need the same answer and would
 * otherwise grow their own copy: the provider sign-in links, which are ordinary
 * anchors the browser follows to the API, and any `src` pointing at an uploaded
 * asset. A second spelling of "where the API is" is a second thing to remember
 * on the day it moves.
 */
export const apiUrl = (path: string): string => `${API_BASE}${path}`

/** Keep low-priority product analytics on the same transport boundary as every other request. */
export async function postProductEvents(body: string, headers: HeadersInit): Promise<boolean> {
  const res = await fetch(apiUrl('/api/product-events'), {
    method: 'POST',
    credentials: 'include',
    keepalive: true,
    headers,
    body,
  })
  return res.ok || res.status === 204
}

export interface Problem {
  file: string
  rule: string
  message: string
  /** Whether `--fix` could repair it. Not the same as whether it may ship. */
  fixable: boolean
  severity: 'error' | 'warning'
  /**
   * The one-press repair the product holds for this problem, if any.
   *
   * Said by the server, because the screen cannot work it out: `fixable` is
   * about the linter's own `--fix` and is false for a redirect that cannot
   * work, which the product repairs exactly. Null for a problem only a person
   * can decide about, which is most of them.
   */
  repair?: 'redirects' | null
}

/** Would this stop the site working? The one question the UI should ask. */
export const isBlocking = (problem: Problem): boolean => problem.severity === 'error'

/**
 * A site build: the step between publishing and a reader seeing it.
 *
 * Worth showing, because when it fails the symptom is that nothing happens —
 * and "I published an hour ago and the site has not changed" needs an answer
 * other than a shrug.
 */
export interface SiteBuild {
  id: string
  sha: string
  status: 'queued' | 'running' | 'done' | 'failed' | 'superseded'
  attempts: number
  error: string | null
  publication: string | null
  queuedAt: string
  finishedAt: string | null
}

/**
 * A shareable build of a branch, for somebody who is not in the product.
 *
 * `path` is present only on the reply that CREATED it: only a hash of the token
 * is stored, so that is the one moment the link exists. A UI that shows it and
 * expects to show it again later is a UI with a copy button that quietly stops
 * working.
 */
export interface PreviewLink {
  id: string
  branch: string
  slug: string
  /** What was built. Null until the queued build has run. */
  sha: string | null
  createdAt: string
  expiresAt: string
  path?: string
}

/** One entry in the record of what readers have been shown. */
export interface Publication {
  id: string
  sha: string
  previousSha: string | null
  branch: string
  message: string
  paths: string[]
  kind: 'publish' | 'rollback'
  /** The publication this one undoes. */
  reverts: string | null
  /** The ref it landed in: the live branch, or `version/<name>`. Null on old rows. */
  intoRef: string | null
  actorName: string | null
  actorEmail: string | null
  createdAt: string
}

export interface StagedChange {
  path: string
  op: 'write' | 'move' | 'delete'
  fromPath: string | null
}

/**
 * What happened to one staged file while the writer was not looking.
 *
 * `clean` and `identical` never reach the interface — the server keeps them
 * out of `conflicts` — because a decision screen listing forty files nobody
 * touched is a screen that gets clicked through rather than read.
 */
export type ConflictKind = 'clean' | 'identical' | 'changed' | 'deleted' | 'added'

/** One file a person has to decide about, with both versions and the diff. */
export interface ConflictFile {
  path: string
  op: 'write' | 'move' | 'delete'
  /** Set when the clash is at the address this row moved the page AWAY from. */
  movedFrom: string | null
  kind: ConflictKind
  base: string | null
  mine: string | null
  theirs: string | null
  /** Theirs -> mine: `-` lines are on the branch, `+` lines are staged here. */
  diff: string
}

/**
 * A commit refused because the branch moved, in full.
 *
 * The counts matter as much as the conflicts: telling somebody that thirty of
 * their thirty-four files replay untouched is what makes the four in front of
 * them feel like a decision instead of a disaster.
 */
export interface ConflictReport {
  branch: string
  expected: string
  actual: string
  conflicts: ConflictFile[]
  replays: string[]
  identical: string[]
}

/** What a resolution did, in the terms it was chosen in. */
export interface ConflictResolution {
  rebasedTo: string
  kept: string[]
  took: string[]
  replays: string[]
  /** How many staged files are left to commit. Zero is a real answer. */
  staged: number
}

/** One deleted page that can still come back. */
export interface TrashedPage {
  id: string
  path: string
  /** From the stored page's own frontmatter; null when it has none. */
  title: string | null
  /** The draft it was deleted on. */
  branch: string
  deletedBy: string
  deletedByName: string | null
  deletedAt: string
}

/**
 * Freshness, per BRAND.md's Pulse triad.
 *
 * `weight` is the whole design: `evidence` means something on the page is
 * demonstrably wrong, `suspicion` means nobody has checked it recently enough
 * to know. Age alone is never evidence, so it can only ever produce `review`.
 */
export interface PulseSignal {
  kind: string
  weight: 'evidence' | 'suspicion'
  /** One sentence, for a person. */
  detail: string
}

/** One page's freshness, as `/api/health` reports it. */
export interface PageHealth {
  path: string
  state: 'current' | 'review' | 'outdated'
  /** 0 to 100, for ranking only — the signals are the real answer. */
  score: number
  signals: PulseSignal[]
  lastChanged: string | null
  lastReviewed: string | null
  nextReview: string | null
  /** Readers in the last 30 days. Null when nothing counted them. */
  readers: number | null
  /** What readers said, when any of them did. */
  helpfulness: { helpful: number; unhelpful: number } | null
  /** Who looks after it, as the address the page names. */
  owner: string | null
  /** Quiet until this moment — the NOTICE only; the row still says overdue. */
  snoozedUntil: string | null
}

export interface PulseSummary {
  total: number
  current: number
  review: number
  outdated: number
  score: number
}

/**
 * What readers said about one published page, from `/api/feedback`.
 *
 * `path` is the page as the READER saw it — a route on the published site,
 * not a repository path — because the footer that collects it only knows
 * where it is standing.
 */
export interface ReaderFeedbackPage {
  path: string
  helpful: number
  notHelpful: number
  comments: { verdict: 'helpful' | 'not_helpful'; comment: string; createdAt: string }[]
}

/**
 * What readers searched for on the published site, from `/api/search-events`.
 *
 * One row per distinct query, already ordered: gaps first — queries whose
 * every search found nothing — then the most searched. `abandoned` counts
 * searches that found results the reader chose none of.
 */
export interface SearchQueryStat {
  query: string
  searches: number
  noResults: number
  clicked: number
  abandoned: number
  isGap: boolean
  lastSearched: string
}

/**
 * One question, however many ways readers spelled it.
 *
 * `query` is the spelling they used most, which is the one to show; every
 * spelling travels with it, because teaching search one word must teach it all
 * of them or the reader who typed the other wording is still failed.
 */
export interface SearchQuestionStat extends SearchQueryStat {
  question: string
  spellings: string[]
}

/**
 * What the published site's traffic looks like, from `/api/analytics`.
 *
 * Every number here is countable and none of it is identifiable — the shapes
 * mirror `@staleless/core`'s, so what the screen renders is exactly what the
 * database can honestly support. `key: null` is kept rather than dropped: an
 * unknown country and a direct arrival are real answers, and removing them
 * would make the rest of the rows add up to the wrong total.
 */
/**
 * One tier, as the server describes it — `@staleless/core`'s `Tier`.
 *
 * `standing` says how true each line is, and the plan card renders on it: a
 * claim marked `on_request` is a conversation rather than a switch that is on,
 * and putting it beside a price with a button would be a promise nobody can
 * keep. Enterprise is the only tier that carries one, which the server holds
 * itself to.
 */
export interface PlanTier {
  name: 'free' | 'pro' | 'business' | 'enterprise'
  label: string
  audience: string
  promise: string
  /** US cents a month. `0` is Free; `null` is "let us talk". */
  monthlyCents: number | null
  buy: 'nothing_to_buy' | 'checkout' | 'contact'
  includes: {
    customCss: boolean
    ourMark: boolean
    ai: { writingPerson: number; writingProject: number; askSite: number }
    collaborators: number | null
    projects: number | null
  }
  adds: { says: string; standing: 'enforced' | 'shipped' | 'on_request' | 'service' }[]
}

export interface AnalyticsSummary {
  days: number
  from: string
  bucket: 'hour' | 'day'
  views: number
  visitors: number
  sessions: number
  series: { at: string; views: number; visitors: number }[]
  pages: { path: string; views: number; visitors: number }[]
  referrers: { key: string | null; views: number }[]
  countries: { key: string | null; views: number }[]
  devices: { key: 'mobile' | 'tablet' | 'desktop' | 'unknown'; views: number }[]
  /**
   * Paths readers asked for that are not there, busiest first.
   *
   * The whole list over the chosen window, not only what crossed the threshold
   * into the Inbox — a person who opened this screen came looking for facts.
   * `referrers` is at most three bare hosts, busiest first, and empty means
   * every request arrived direct: a bookmark, a typed address, or a client
   * that hides where it came from.
   */
  missed: { path: string; misses: number; readers: number; referrers: string[]; lastAt: string }[]
  /**
   * When a missed path stops being a row here and becomes an item in the
   * Inbox. Sent rather than hard-coded, for the same reason `retentionDays`
   * is: the screen states a rule it does not own, and this bundle cannot
   * import the package that owns it.
   */
  missedRule: { misses: number; readers: number; days: number }
  /** How long this project keeps its views. Stated on the screen. */
  retentionDays: number
  /** Whether anything is held at all, so an empty screen can advise correctly. */
  everRecorded: boolean
}

/** A token a workspace minted. Never carries the secret. */
/**
 * Where a customer's own domain stands, and what is still in the way.
 *
 * Three separate truths, because they fail separately and a person fixes them
 * in a different place each time: the TXT record proves the hostname is theirs,
 * the address record decides whether anything arrives, and the certificate
 * decides whether a browser will show it. `state` is only the first of the
 * three — the other two are looked up when somebody presses check, and are null
 * until they have been.
 */
export interface DomainStatus {
  domain: string | null
  state: 'unconfigured' | 'pending' | 'verified' | 'error'
  /** The proof record alone, while it is still being asked for. */
  record: { name: string; type: 'TXT'; value: string } | null
  /** Everything to publish: the proof, and where readers are sent. */
  records: {
    name: string
    type: 'TXT' | 'CNAME' | 'A' | 'AAAA'
    value: string
    purpose: 'ownership' | 'address'
  }[]
  checkedAt: string | null
  /** The FIRST thing in the way, in words. Stored, so it survives a reload. */
  problem: string | null
  address: { ok: boolean; resolved: string[]; problem: string | null } | null
  certificate: {
    ok: boolean
    expiresAt: string | null
    issuer: string | null
    problem: string | null
  } | null
  /** What the certificate reconciler last did for this hostname, if it has looked. */
  provisioning: {
    hostname: string
    outcome: 'issued' | 'kept' | 'refused' | 'failed' | 'waiting' | 'removed'
    failures: number
    lastAt: string
    nextAt: string | null
    problem: string | null
    runAt: string
  } | null
}

export interface ApiToken {
  id: string
  name: string
  /** The visible first characters, enough to recognise one in a log. */
  prefix: string
  scopes: string[]
  expiresAt: string
  lastUsedAt: string | null
  revokedAt: string | null
  createdName: string
  createdAt: string
}

export interface BranchInfo {
  name: string
  status: string
  reviewStatus?: ReviewStatus
  headSha: string | null
  prNumber: number | null
  prUrl: string | null
  /** The published ref this draft lands in. Null is the live documentation. */
  targetRef?: string | null
}

/**
 * One navigation entry: a page, a group, or a link out of the site.
 *
 * Loose rather than a union, deliberately. This is what arrived over the wire
 * from a file somebody may have hand-edited, and the strict version lives in
 * `@staleless/schema` where the server validates it — a second strict copy here
 * would refuse to RENDER a file the server already accepted, which is the worst
 * of both.
 */
export interface NavItem {
  page?: string
  label?: string
  icon?: string
  /** An absolute URL. Present on an external link, and then `page` is not. */
  href?: string
  items?: NavItem[]
  /**
   * A separator: a rule between items, with `label` as its optional caption.
   * Nothing else is set on one — it names no page and contains nothing.
   */
  separator?: true
}

/** What an import did, and — just as important — what it could not do. */
export interface ImportReport {
  /**
   * Every platform the importer can read. This listed four while the service
   * read eight — Nextra, Notion, Confluence and a folder of HTML all arrived
   * as something the type said could not exist, and the screen offered none of
   * them. `packages/db/test/column-lists.test.ts` holds the database's list to
   * the service's; this is the third copy, and the reason it is written out
   * rather than imported is that the writer is built separately.
   */
  platform:
    'gitbook' | 'docusaurus' | 'mintlify' | 'nextra' | 'notion' | 'confluence' | 'html' | 'markdown'
  branch: string
  pages: number
  images: number
  /** Shared blocks carried in beside the pages that reference them. */
  snippets: number
  imageBytes: number
  skipped: string[]
  unresolvedImages: number
  rejectedFiles: { path: string; reason: string }[]
  notes: { kind: string; rule: string; message: string; count: number; pages: number }[]
  navReplaced: boolean
  /**
   * Old addresses carried from the source site's own configuration.
   *
   * `beside` when the project already had redirects, so these landed in
   * `redirects.suggested.yaml` rather than over rules somebody wrote.
   */
  redirects: { carried: number; beside: boolean }
}

/**
 * An import, as a thing that is happening rather than a thing that happened.
 *
 * Importing used to be a request: you pressed the button and either got a
 * report or lost the run. A real documentation site takes minutes to convert,
 * so the state has to live somewhere a reload can find it — which is why this
 * is fetched rather than held in a component, and why every field the screen
 * needs is on the server's record.
 */
export interface ImportJob {
  id: string
  /** `failed` is the one that needs a person. */
  status: 'queued' | 'running' | 'done' | 'failed'
  platform: ImportReport['platform'] | null
  archiveName: string
  archiveBytes: number
  startedName: string
  attempts: number
  /** Null until the archive has been unpacked and walked. */
  pagesTotal: number | null
  pagesDone: number
  /** What it is converting right now, so progress has a name and not just a bar. */
  currentFile: string | null
  branch: string | null
  /**
   * The version this import was aimed at, as a git ref, or null for the live
   * documentation. The screens name it, because somebody who deliberately sent
   * an import into `v1` should be able to see that it went there.
   */
  targetRef: string | null
  /** In plain words, with what to do about it. Null when nothing failed. */
  error: string | null
  /** The same failure for whoever has to debug it. */
  errorDetail: string | null
  report: ImportReport | null
  queuedAt: string
  startedAt: string | null
  finishedAt: string | null
  /** Whether the retry door is open: it gave up, and there is work left. */
  retryable: boolean
}

/**
 * One file in the workspace's asset library.
 *
 * `url` is content-addressed — the same bytes always produce the same URL — so
 * it never changes and is safe to cache forever. `width`/`height` are null for
 * a file the sniffer could not measure, such as a PDF.
 */
export interface VariableRow {
  name: string
  value: string
  /** Pages whose text references {{name}} — the impact a changed value lands on. */
  pages: string[]
}

export interface AssetRecord {
  id: string
  key: string
  url: string
  /** A small derived copy for the grid, or null when there is not one. */
  thumbUrl: string | null
  contentType: string
  bytes: number
  width: number | null
  height: number | null
  originalName: string | null
  altText: string | null
  createdAt: string
}

/** Which pages reference an asset, by repository path. */
export interface AssetUsage {
  pages: string[]
}

/**
 * One row of the audit trail: a sensitive operation, who did it, and when.
 *
 * `action` is the enum the server stores, not words for a screen — see
 * `describeAudit` in `SettingsScreen.tsx` for the translation. `payload` is
 * whatever detail that action carries (a branch, a domain, an email); it is
 * typed loosely for the same reason `Suggestion.evidence` is: a new audited
 * action should not need this file changed before it can show what it did.
 */
export interface AuditRow {
  id: string
  action: string
  actorName: string | null
  actorKind: 'human' | 'mcp'
  commitSha: string | null
  payload: Record<string, unknown>
  createdAt: string
}

/** One signed-in browser or device, from the security page. */
export interface DeviceSession {
  id: string
  userAgent: string | null
  region: string | null
  createdAt: string
  lastActiveAt: string
  /** This is the session the request arrived on. */
  current: boolean
}

/** One documentation site in a workspace. Each has its own repository. */
export interface SearchHit {
  path: string
  title: string
  snippet: string
  matchStart: number
  matchLength: number
  anchor: string
  heading: string
}

export interface ProjectSummary {
  id: string
  slug: string
  name: string
  contentRepo: string | null
  defaultBranch: string
  visibility: 'public' | 'password' | 'workspace'
  /** The connected customer hostname, when configured for this project. */
  domain?: string | null
  /**
   * Where this project's published site answers.
   *
   * Per project, because a workspace's second documentation site answers under
   * its own path: one address for all of them opened the FIRST project's site
   * from every other one, which is the site it is easiest to mistake it for.
   * Null only if the deployment could not say.
   */
  siteUrl?: string | null
}

export interface State {
  repo: string
  /** Main and the last deployed commit; `ahead` means Publish main has work to send. */
  release: {
    branch: string
    headSha: string
    publishedSha: string | null
    ahead: boolean
  }
  /**
   * The four Pulse counts, when `include: 'health'` asked for them, and null
   * otherwise. Home's tiles; the Pulse screen uses `/api/health`, which
   * answers the per-page list as well.
   */
  health: { summary: PulseSummary; withoutDates: boolean } | null
  /** Where the published site is served, when it is configured. */
  siteUrl: string | null
  branch: string | null
  branches: BranchInfo[]
  /** `title` is the page's own, from its frontmatter. Null when it has none. */
  pages: { path: string; id: string; title: string | null }[]
  nav: { sections: { label: string; items: NavItem[] }[] }
  navRaw: string
  staged: StagedChange[]
  lint: { problems: Problem[]; blocking: number }
  /**
   * Who this person is in this workspace. Null for the service actor, which has
   * no membership and therefore no role.
   */
  you: {
    role: Role
    canPublish: boolean
    canReview?: boolean
    canManageBilling?: boolean
    /**
     * The role in the WORKSPACE, before this project's override.
     *
     * `role` above says what this person may do in the documentation site they
     * are looking at; this says what they may do to the workspace — invite
     * somebody, make a project, read the audit trail. A project override is not
     * a promotion, and offering the workspace's own controls from it would offer
     * buttons the API refuses. Optional, because a deployment answering an older
     * shape should not blank the screen.
     */
    workspaceRole?: Role
  } | null
  /**
   * Whether this project may ask a model for help with the words (§22).
   *
   * Optional so an older deployment does not blank the screen; absent means
   * no, which is also the default the switch starts at.
   */
  writingHelp?: boolean
}

/**
 * Frontmatter, from the schema rather than restated here.
 *
 * There used to be a copy: seven fields, listed by hand, exactly the seven the
 * SERIALIZER also listed by hand while the schema declared sixteen. Three copies
 * of one shape, and all three were wrong in the same direction — the writer
 * could not offer a field it did not know existed, so `last_reviewed`, the thing
 * this product is named after, had no control anywhere in the UI.
 */
import type { Frontmatter } from '@staleless/schema'
export type { Frontmatter }

/**
 * A suggestion, as the inbox stores it.
 *
 * `evidence` and `pages` are typed loosely on purpose. They are an auditable
 * payload written by whichever analyser produced the finding, and the UI's job
 * is to show them rather than to understand them — a shape it insisted on would
 * mean a new analyser could not display its own reasoning until this file was
 * changed too.
 */
export type SuggestionState =
  'open' | 'applied' | 'ignored' | 'not_relevant' | 'snoozed' | 'resolved'

export type SuggestionAction = 'apply' | 'ignore' | 'not-relevant' | 'snooze' | 'reopen'

export interface Suggestion {
  id: string
  fingerprint: string
  trigger: string
  severity: 'breaking' | 'potentially-breaking' | 'additive' | 'info' | string
  confidence: 'high' | 'medium' | 'low' | null
  title: string
  detail: string
  evidence: { rule?: string; matched?: string; line?: number; detail?: string; weight?: string }[]
  pages: { path?: string; confidence?: string }[]
  state: SuggestionState
  snoozedUntil: string | null
  firstSeenAt: string
  lastSeenAt: string
}

export type Role = 'owner' | 'admin' | 'editor' | 'viewer'

/** What this workspace's documentation is for, answered on the first run. */
export type UseCase = 'product' | 'api' | 'help' | 'handbook'

/**
 * The four kinds, as a control says them.
 *
 * Here rather than in a screen because two screens ask the same question — the
 * Home checklist and the command palette — and two copies of four labels is
 * two answers to "what is this called" waiting to disagree.
 */
/**
 * What each role is called where one is CHOSEN, and what it does.
 *
 * Here rather than in a screen because three screens offer the same four
 * choices — the first run's invitation, the People roster, and the project
 * override beside it — and three copies of four labels is three answers to
 * "what does Editor mean" waiting to disagree. They already did: the first run
 * said "Editor — writes, and publishes with you" while the roster said
 * "editor", which is a different promise and a lowercase noun.
 *
 * Short enough for an option, and it says what the role DOES: "Editor" is a
 * word whose meaning is obvious to whoever picked it and to nobody else, and
 * the menu is exactly where somebody is deciding how much of their
 * documentation another person may change.
 */
export const ROLE_LABELS: Record<Role, string> = {
  owner: 'Owner — everything, including people',
  admin: 'Admin — writes and manages settings',
  editor: 'Editor — writes on drafts',
  /*
   * Reads AND comments. "Reads only" was wrong in the direction that matters:
   * it understated the role, so an owner deciding whether somebody may join a
   * conversation about a page was told they could not. A viewer may comment —
   * `apps/api/test/write-permissions.test.ts` drives one doing it, and the
   * permission ledger there records the rule as spec 7.
   */
  viewer: 'Viewer — reads and comments',
}

export const USE_CASE_LABELS: Record<UseCase, string> = {
  product: 'Product docs',
  api: 'API reference',
  help: 'Help centre',
  handbook: 'Handbook',
}

/**
 * Where the guided first run stands. `shouldOnboard` is the server's decision
 * — it alone knows who created the workspace and whether the run already ended
 * — so the client shows the sequence when told to and never re-derives it.
 */
export interface OnboardingStatus {
  useCase: UseCase | null
  onboardedAt: string | null
  shouldOnboard: boolean
  /** Whether another person can be invited under the workspace's current plan. */
  mayInvite: boolean
}

/** A change to a page's fields. `null` removes one; see `api.frontmatter`. */
export type FrontmatterPatch = { [K in keyof Frontmatter]?: Frontmatter[K] | null }

/** One redirect, with where it lands once the pages are taken into account. */
export interface RedirectRow {
  from: string
  to: string
  status: 301 | 302 | 308
  /** `page`, `nowhere`, `offsite`, or `wildcard` when it cannot be checked. */
  lands?: 'page' | 'nowhere' | 'offsite' | 'wildcard'
}

export interface Member {
  userId: string
  email: string
  displayName: string
  role: Role
  canPublish: boolean
  canReview?: boolean
  canManageBilling?: boolean
  joinedAt: string
  /**
   * Whether they have a confirmed second step.
   *
   * Read on the roster so the switch that requires one is not a decision made
   * blind: an owner about to turn it on can see exactly who it will send to
   * an enrolment screen. Optional, so an older deployment does not blank the
   * list; absent reads the same as false and the sentence says "we cannot
   * tell" rather than counting.
   */
  hasSecondFactor?: boolean
}

export interface Invitation {
  id: string
  email: string
  role: Role
  canPublish: boolean
  canReview?: boolean
  canManageBilling?: boolean
  expiresAt: string
  state: 'pending' | 'accepted' | 'revoked' | 'expired'
  createdAt: string
}

export interface Workspace {
  id: string
  slug: string
  name: string
  role: Role
  /**
   * Whether this person may publish here.
   *
   * A separate grant rather than a fifth role, because plenty of teams want
   * editors who write freely and a smaller set who decide what readers see.
   * Optional because /auth/login answers with the workspace it landed in before
   * the grant is looked up.
   */
  canPublish?: boolean
  canManageBilling?: boolean
}

export type ReviewStatus = 'none' | 'requested' | 'changes_requested' | 'approved'
export interface ReviewEvent {
  id: string
  kind: 'request' | 'comment' | 'approve' | 'request_changes'
  actorName: string
  actorId: string | null
  body: string | null
  headSha: string | null
  createdAt: string
}
export interface ReviewThread {
  status: ReviewStatus
  authorId: string
  /**
   * The commit this thread was read at — what the screen showing it is about.
   *
   * Sent back with a verdict, so the server can refuse an answer formed on a
   * draft the author has since committed to. Null on a draft with nothing
   * committed, which is a draft no verdict is allowed on anyway.
   */
  headSha: string | null
  events: ReviewEvent[]
}

/** A comment thread on a page, with its anchor recomputed for this body. */
export interface CommentThread {
  id: string
  parentId: string | null
  authorName: string
  authorId: string | null
  body: string
  quote: string | null
  createdAt: string
  resolvedAt: string | null
  resolvedBy: string | null
  anchor: { state: 'anchored' | 'ambiguous' | 'orphaned'; at: number | null }
  replies: CommentThread[]
}

/** Somebody is waiting on you — in-app, per person. */
export interface NotificationRecord {
  id: string
  /**
   * Every kind the server can send.
   *
   * This listed four while the server sent eight — `published`,
   * `publish_failed`, `webhook_disabled` and `review_due` all arrived as
   * something the type said could not exist. Nothing broke, because the panel
   * only printed the subject, which is exactly why it went unnoticed.
   */
  kind:
    | 'mention'
    | 'reply'
    | 'comment'
    | 'impact'
    | 'review_requested'
    | 'review_verdict'
    | 'published'
    | 'publish_failed'
    | 'webhook_disabled'
    | 'review_due'
    | 'domain_verified'
    | 'ai_allowance'
    | 'plan_ended'
    | 'payment_failed'
    | 'conflict'
    | 'owner_changed'
    | 'certificate_failed'
  subject: string
  pagePath: string | null
  branch: string | null
  actorName: string
  createdAt: string
  readAt: string | null
}

export interface Session {
  user: {
    id: string
    email: string
    displayName: string
    emailVerifiedAt: string | null
    emailNotifications: boolean
    /** The weekly summary, on its own switch. See migration 0065. */
    digest: 'weekly' | 'off'
  }
  workspaces?: Workspace[]
  workspace?: Workspace
  /**
   * The workspace that requires a second step this person does not have.
   *
   * Null for almost everybody. When it is set the session is real — it was
   * issued, and enrolling needs one — but the server refuses everything under
   * `/api/` until an authenticator is confirmed, so the shell shows the
   * enrolment screen and nothing else. Named, because the screen says whose
   * decision it was.
   */
  enrolTwoFactor?: { slug: string; name: string } | null
}

/**
 * Which workspace the next request acts in.
 *
 * The server resolves this per request from the session's memberships, and takes
 * `x-workspace` as an override. Without the header it uses the first membership,
 * which is right for the one workspace most people have and arbitrary for
 * anyone who belongs to two.
 *
 * Kept here rather than in a component so that no caller has to remember it.
 * A missed header would not error — it would quietly read and WRITE in the
 * wrong workspace, which is the worst kind of bug this code can have.
 */
/**
 * Which project inside that workspace.
 *
 * A workspace holds several documentation sites — Adalo would keep Ada, Classic
 * and Studio apart — and each has its own repository. Remembered the same way
 * the workspace is, and for the same reason: a missed header would quietly read
 * and WRITE in the wrong project, which is somebody editing the wrong site
 * while believing otherwise.
 *
 * Remembered per workspace, because a project slug only means anything inside
 * one: switching workspace must not carry the old workspace's project with it.
 */
const PROJECT_KEY = 'staleless:project'
let currentProject: string | null = null

function projectKeyFor(workspace: string | null): string {
  return `${PROJECT_KEY}:${workspace ?? 'default'}`
}

export function setProject(slug: string | null): void {
  currentProject = slug
  try {
    if (slug) localStorage.setItem(projectKeyFor(currentWorkspace), slug)
    else localStorage.removeItem(projectKeyFor(currentWorkspace))
  } catch {
    // A locked-down browser still works; it just forgets between visits.
  }
}

export function activeProject(): string | null {
  return currentProject
}

const WORKSPACE_KEY = 'staleless:workspace'
let currentWorkspace: string | null = null

/** Read the remembered choice. Storage can throw in a locked-down browser. */
function rememberedWorkspace(): string | null {
  try {
    return localStorage.getItem(WORKSPACE_KEY)
  } catch {
    return null
  }
}

export function workspaceSlug(): string | null {
  return currentWorkspace
}

/**
 * The draft the editor currently shows, for calls made from places that have
 * no way to receive it as a prop — a node view deep inside the editor asking
 * "which snippets exist?" must ask about the DRAFT's tree, or a snippet
 * created a moment ago is invisible to the picker. The same module-level
 * pattern as the workspace slug above, and set from the same place: the app
 * shell, whenever the draft changes.
 */
let currentDraft: string | null = null
export function setCurrentDraft(name: string | null): void {
  currentDraft = name
}

export function setWorkspaceSlug(slug: string | null): void {
  currentWorkspace = slug
  /**
   * Adopt that workspace's remembered project, and forget the old one.
   *
   * A project slug only means something inside one workspace, so carrying it
   * across would send `x-project: classic` to a workspace that has no such
   * project. The server answers 404 rather than guessing, which is right, but
   * the person would just see the app fail to load.
   */
  try {
    currentProject = slug ? localStorage.getItem(projectKeyFor(slug)) : null
  } catch {
    currentProject = null
  }
  try {
    if (slug) localStorage.setItem(WORKSPACE_KEY, slug)
    else localStorage.removeItem(WORKSPACE_KEY)
  } catch {
    // A remembered choice is a convenience; failing to remember it is not an
    // error worth showing anybody. The session still acts in `currentWorkspace`.
  }
}

/**
 * Settle on a workspace for this session.
 *
 * Only ever chooses from what the server says the person belongs to, so the
 * header can never ask for a workspace they are not in. A remembered slug that
 * is no longer in the list is dropped rather than sent — losing access to a
 * workspace should not leave the app wedged on a 403.
 */
export function adoptWorkspace(session: Session): Workspace | null {
  // `workspace` is the fallback because /auth/login and /auth/signup name where
  // this session landed, and only /auth/me used to carry the full list. Both
  // return `workspaces` now; this keeps a client that meets an older server
  // acting in the right place rather than in none.
  const list = session.workspaces ?? (session.workspace ? [session.workspace] : [])
  if (list.length === 0) {
    setWorkspaceSlug(null)
    return null
  }
  const remembered = rememberedWorkspace()
  const chosen = list.find((w) => w.slug === remembered) ?? list[0]!
  setWorkspaceSlug(chosen.slug)
  return chosen
}

/**
 * What the password step answers with when the account has a second factor.
 *
 * No user, no workspaces, no cookie — nothing but a receipt saying the
 * password was right just now, which only the code step can spend.
 */
export interface TwoFactorChallenge {
  twoFactor: true
  challenge: string
  /** Seconds. Shown so nobody wonders why a half-finished sign-in went stale. */
  expiresIn: number
}

/** Narrow the password step's answer: a session, or a code still to come. */
export function needsCode(result: Session | TwoFactorChallenge): result is TwoFactorChallenge {
  return (result as TwoFactorChallenge).twoFactor === true
}

export interface TwoFactorStatus {
  /** Confirmed, and therefore standing between a password and a session. */
  enabled: boolean
  /** Started and never proved. Gates nothing. */
  pending: boolean
  recoveryCodesLeft: number
}

/**
 * A URL this project tells when something happens.
 *
 * No secret field, and that is structural rather than an omission: the signing
 * key is legible exactly once, in the reply that created the endpoint, and a
 * type with nowhere to put it is a screen that cannot leak it by accident.
 */
export interface WebhookEndpoint {
  id: string
  url: string
  enabled: boolean
  /** Why it is off — ours or theirs. Null while it is on. */
  disabledReason: string | null
  disabledAt: string | null
  /** Dead letters in a row. Zero after any success. */
  consecutiveFailures: number
  createdName: string
  createdAt: string
  lastDeliveryAt: string | null
  lastSuccessAt: string | null
}

/**
 * One handover of one event to one endpoint — the queue row and the log line
 * are the same thing, so what a person reads is what the worker acted on
 * rather than a summary of it.
 */
export interface WebhookDelivery {
  id: string
  endpointId: string | null
  /** Frozen at the time, so a delivery can say where it went after the
      endpoint has been removed. */
  url: string
  event: string
  status: 'queued' | 'running' | 'done' | 'failed'
  attempts: number
  /** What the receiver answered. Null when nothing did. */
  responseStatus: number | null
  durationMs: number | null
  /** In a person's words. Null when it worked. */
  error: string | null
  errorDetail: string | null
  payload: unknown
  queuedAt: string
  nextAttemptAt: string
  finishedAt: string | null
}

/** Thrown when the server says we are not signed in, so the UI can show the gate. */
/**
 * Told once, from wherever the refusal lands.
 *
 * Registered by the app shell, the same way the re-authentication prompt is:
 * this module cannot hold React state, and the app cannot wrap a hundred call
 * sites.
 */
let accessLost: (() => void) | null = null
export function setAccessLostHandler(handler: (() => void) | null): void {
  accessLost = handler
}

export class NotSignedIn extends Error {
  constructor() {
    super('not signed in')
    this.name = 'NotSignedIn'
  }
}

/**
 * How the interface asks "is that really you?".
 *
 * When a sensitive request is refused with `code: 'reauth_required'`, the
 * registered prompt is shown — the ConfirmIdentity dialog — and a `true`
 * answer means the password was verified and the ORIGINAL request should be
 * retried. Retrying here, underneath the components, is what keeps the
 * person's pending input intact: the settings screen that sent a new domain
 * never learns the request was refused once, so nothing it holds gets reset.
 *
 * One prompt at a time: if two refusals land together they share one dialog
 * and one answer, rather than stacking two password prompts.
 */
export type ReauthPrompt = (info: { passwordless: boolean }) => Promise<boolean>
let reauthPrompt: ReauthPrompt | null = null
let reauthPending: Promise<boolean> | null = null

export function setReauthPrompt(prompt: ReauthPrompt | null): void {
  reauthPrompt = prompt
}

async function request<T>(path: string, init?: RequestInit, retried = false): Promise<T> {
  const res = await fetch(apiUrl(path), {
    ...init,
    /**
     * The session cookie travels on every request.
     *
     * Said explicitly because the default does not do it. `same-origin` means
     * a cross-origin fetch sends NO cookies — not a stale one, not an
     * anonymous one, none — so the API sees a stranger and answers 401, and
     * the screen shows the sign-in gate to somebody who is perfectly signed
     * in. That is true even when the two hosts are the same SITE, which
     * `app.staleless.com` and `api.staleless.com` are: `SameSite=Lax` decides
     * whether the browser is willing to send the cookie, and this decides
     * whether it is asked to.
     *
     * Same-origin behaviour is unchanged by it, which is what makes the rule
     * cheap: every request, no exceptions to remember.
     */
    credentials: 'include',
    headers: {
      'content-type': 'application/json',
      // Ask the server to act in the workspace the UI is showing. Sent on the
      // auth routes too, where it is ignored: one rule is easier to keep true
      // than a rule with an exception.
      ...(currentWorkspace ? { 'x-workspace': currentWorkspace } : {}),
      ...(currentProject ? { 'x-project': currentProject } : {}),
      ...(init?.headers ?? {}),
    },
  })
  /**
   * Signing in and signing up are the two requests made BEFORE there is a
   * session, so a 401 from either of them is the server's answer about the
   * credentials just typed — not a sign that one has lapsed. Folding it into
   * `NotSignedIn` here threw away that answer and replaced it with the literal
   * string "not signed in", which the sign-in form then printed back at
   * somebody who had just typed a wrong password: true, and useless. Every
   * other route keeps the short-circuit, because there a 401 really does mean
   * the gate belongs on screen.
   */
  // `/auth/reauth` belongs in this list for the same reason: its 401 is the
  // answer about the password just typed into the confirm dialog, not a lapsed
  // session — swallowing it into NotSignedIn would flash the sign-in gate at
  // somebody who merely mistyped.
  const isCredentialAttempt =
    path === '/auth/login' ||
    path === '/auth/signup' ||
    path === '/auth/reauth' ||
    /*
     * And the two doors that ask for the current password from INSIDE a
     * session: changing the password, and moving the address the account signs
     * in with. Their 401 is the answer about what was just typed — read as
     * "not signed in" it replaces "that is not your current password" with the
     * sign-in gate, throwing away a session that is perfectly good. Found by a
     * gate that mistyped a password on purpose and was told it was not signed
     * in.
     */
    path === '/auth/password/change' ||
    path === '/auth/email/change' ||
    // The code step's 401 is the answer about the six digits just typed. Read
    // as "not signed in" it would replace a wrong-code message with the
    // sign-in gate, throwing away the challenge and the person's place in it.
    path === '/auth/login/totp'
  if (res.status === 401 && !isCredentialAttempt) throw new NotSignedIn()
  const text = await res.text()
  const body = text ? (JSON.parse(text) as unknown) : {}
  if (!res.ok) {
    const shape = typeof body === 'object' && body !== null ? (body as Record<string, unknown>) : {}
    const message = 'error' in shape ? String(shape['error']) : `${res.status} ${res.statusText}`
    /**
     * A sensitive change on a session that has not proven itself recently.
     * Ask the person to confirm — once — and replay the original request
     * exactly as it was. `retried` stops a second lap: if the retry is refused
     * the same way, something is wrong beyond what another prompt would fix,
     * and the refusal should reach the screen.
     */
    if (res.status === 403 && shape['code'] === 'reauth_required' && reauthPrompt && !retried) {
      const confirmed = await (reauthPending ??= reauthPrompt({
        passwordless: shape['passwordless'] === true,
      }).finally(() => {
        reauthPending = null
      }))
      if (confirmed) return request<T>(path, init, true)
    }
    /**
     * This session is not in the workspace it is acting in.
     *
     * Reported here rather than at each call site, because the discovery can
     * arrive at any of a hundred of them — the tree, the inbox, Pulse, a save
     * — and only one of them used to notice. Every other one printed the
     * refusal as an ordinary error, which reads as a product that has broken
     * rather than a team somebody has been taken out of.
     *
     * The refusal is still thrown: the caller's own error handling is not this
     * function's business, and the screen that replaces it takes a moment to
     * arrive.
     */
    if (res.status === 403 && shape['code'] === 'not_a_member') accessLost?.()
    // Carrying the whole body, not just the sentence. Several refusals here are
    // answerable — "these pages link to it", "confirm your address first" — and
    // a UI that only has the sentence can only print it.
    throw new RequestFailed(message, res.status, shape)
  }
  return body as T
}

/**
 * A request the server refused, with what it said.
 *
 * The code and the body matter: a refusal like "these pages link to it" is a
 * question the UI can put to somebody, and it cannot do that with a sentence
 * alone.
 */
export class RequestFailed extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly body: Record<string, unknown>,
  ) {
    super(message)
    this.name = 'RequestFailed'
  }

  get code(): string | undefined {
    return typeof this.body['code'] === 'string' ? this.body['code'] : undefined
  }
}

/**
 * How long to wait before trying a refused request again, or null for never.
 *
 * Only for the content API's own budget, and only that: a refusal that means
 * "you are asking too fast" has a number attached and is worth retrying by
 * itself, while every other failure means somebody has to decide something.
 * The clamp is the honesty — a server that answered `retryAfter: 86400` would
 * otherwise leave an editor waiting a day for a save it could try in a minute,
 * and one that answered 0 would spin.
 */
export function retryDelaySeconds(error: unknown): number | null {
  if (!(error instanceof RequestFailed) || error.code !== 'rate_limited') return null
  const said = Number(error.body['retryAfter'] ?? 0)
  if (!Number.isFinite(said)) return 1
  return Math.min(60, Math.max(1, Math.round(said)))
}

const qs = (params: Record<string, string | undefined>): string => {
  const search = new URLSearchParams()
  for (const [k, v] of Object.entries(params)) if (v) search.set(k, v)
  const s = search.toString()
  return s ? `?${s}` : ''
}

export const api = {
  /* ---- who am I ------------------------------------------------------- */

  me: () => request<Session>('/auth/me'),

  /**
   * The password step. It does NOT always produce a session.
   *
   * An account with two-step sign-in answers with a challenge instead, and no
   * cookie is set — which is the whole point of the second factor. Callers
   * branch with `needsCode`.
   */
  signIn: (email: string, password: string) =>
    request<Session | TwoFactorChallenge>('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    }),

  /** The code step: spend the challenge, get the session. */
  signInWithCode: (challenge: string, code: string) =>
    request<Session & { usedRecoveryCode: boolean; recoveryCodesLeft: number | null }>(
      '/auth/login/totp',
      { method: 'POST', body: JSON.stringify({ challenge, code }) },
    ),

  signUp: (input: {
    email: string
    password: string
    displayName: string
    /** Absent when `invitation` is set: they are joining, not starting one. */
    organisationName?: string
    /**
     * The token they arrived on, spent in the same transaction that creates
     * the account — so a wrong address leaves no account behind to explain.
     */
    invitation?: string
  }) => request<Session>('/auth/signup', { method: 'POST', body: JSON.stringify(input) }),

  /**
   * What an invitation link says about itself, before anybody signs in.
   *
   * The gate calls this so it can name the workspace and the address instead
   * of showing a stranger a blank form with an "Organisation name" field on it.
   */
  describeInvitation: (token: string) =>
    request<{
      invitation: {
        workspace: string
        email: string
        role: Role
        canPublish: boolean
        canReview?: boolean
        canManageBilling?: boolean
        state: 'pending' | 'accepted' | 'revoked' | 'expired'
        /** Whether this workspace requires two-step sign-in, said before the
         *  link is accepted rather than a minute after. */
        requireTwoFactor?: boolean
      }
    }>(`/auth/invitation?token=${encodeURIComponent(token)}`),

  signOut: () => request<{ ok: true }>('/auth/logout', { method: 'POST' }),

  /** Which sign-in doors exist. Safe before any session. */
  providers: () =>
    request<{ password: boolean; google: boolean; github: boolean }>('/auth/providers'),

  /**
   * Ask for a password-reset link.
   *
   * Always answers `ok`, whether or not that address has an account — the form
   * must not become a way to find out who does. The caller can only promise
   * that the request went through.
   */
  forgotPassword: (email: string) =>
    request<{ ok: true }>('/auth/password/forgot', {
      method: 'POST',
      body: JSON.stringify({ email }),
    }),

  /**
   * Ask for a link that signs you in, with no password at all.
   *
   * The same silence as the reset above, and for the same reason. The door for
   * somebody who never chose a password — an invited colleague, or an account
   * that has only ever arrived through Google.
   */
  requestSignInLink: (email: string) =>
    request<{ ok: true }>('/auth/signin/link', {
      method: 'POST',
      body: JSON.stringify({ email }),
    }),

  /**
   * Spend one. Answers exactly what `/auth/login` answers — a session, or a
   * challenge when the account has an authenticator, because a link does not
   * walk past one.
   */
  signInWithLink: (token: string) =>
    request<Session | TwoFactorChallenge>('/auth/signin/link/consume', {
      method: 'POST',
      body: JSON.stringify({ token }),
    }),

  /**
   * Spend the link and choose a new password. Every session is revoked by it,
   * including this browser's, so what follows is signing in again — which is
   * the right ending: a password nobody can prove they know is not a session.
   */
  resetPassword: (token: string, password: string) =>
    request<{ ok: true }>('/auth/password/reset', {
      method: 'POST',
      body: JSON.stringify({ token, password }),
    }),

  /**
   * The challenge a provider sign-in still owes a code for.
   *
   * The callback sets a short-lived cookie and sends the browser back to the
   * sign-in screen naming the provider; this reads it once. `null` means there
   * is nothing pending, which is what a reload after finishing looks like.
   */
  oauthSecondFactor: (provider: 'google' | 'github' = 'google') =>
    request<{ challenge: string; expiresIn: number }>(`/auth/${provider}/second-factor`).catch(
      () => null,
    ),

  /**
   * Finish a first sign-in through a provider.
   *
   * The provider knows who somebody is and cannot know what to call their
   * workspace, so the claims ride back from the callback in a signed cookie
   * and this asks for the one thing missing. The cookie is what authorises it;
   * there is no session yet. The provider is named because the cookie is:
   * finishing a GitHub sign-in against the Google door reads no claims at all.
   */
  finishOAuth: (organisationName: string, provider: 'google' | 'github' = 'google') =>
    request<{ ok: true }>(`/auth/${provider}/finish`, {
      method: 'POST',
      body: JSON.stringify({ organisationName }),
    }),

  /** Every browser or device currently signed in as you, newest activity first. */
  sessions: () => request<{ sessions: DeviceSession[] }>('/auth/sessions'),

  /**
   * Sign a session out. Revoking the one this request arrived on clears the
   * cookie too, so the caller should expect to be signed out of THIS browser
   * when `id` is the current session.
   */
  revokeSession: (id: string) =>
    request<{ ok: true }>(`/auth/sessions/${id}`, { method: 'DELETE' }),

  /**
   * Ask for a failed build again.
   *
   * The queue gives up after three attempts and says why. Until this existed
   * there was no way back through that: a build that failed for a reason that
   * has since passed could only be re-run by publishing something else.
   */
  retryBuild: (id: string) =>
    request<{ queued: true }>(`/api/builds/${id}/retry`, { method: 'POST' }),

  /**
   * Rebuild the current published site without publishing content.
   *
   * Used after settings that are baked into the static reader, such as Ask.
   */
  rebuildSite: () =>
    request<{ queued: true; build: string | null }>('/api/builds/rebuild', { method: 'POST' }),

  /**
   * Change the name that appears on your work — on every revision, in the
   * people list, in the audit trail. The address is not changeable here: it is
   * the sign-in identity, and moving it needs the new one verified.
   */
  updateProfile: (changes: {
    displayName?: string
    emailNotifications?: boolean
    digest?: 'weekly' | 'off'
  }) =>
    request<{ displayName: string; emailNotifications: boolean; digest: 'weekly' | 'off' }>(
      '/auth/profile',
      { method: 'PATCH', body: JSON.stringify(changes) },
    ),

  /**
   * Change the password from inside a session.
   *
   * Asks for the current one, because a signed-in browser somebody walked away
   * from must not be enough to take the account — and every OTHER session ends,
   * which is the point of changing it after it has been somewhere it should
   * not.
   */
  changePassword: (current: string, next: string) =>
    request<{ ok: true }>('/auth/password/change', {
      method: 'POST',
      body: JSON.stringify({ current, next }),
    }),

  /**
   * Confirm the current password mid-session, refreshing this session's proof
   * of identity. Sent by the ConfirmIdentity dialog when a sensitive change is
   * refused with `reauth_required`; never stored anywhere on this side.
   */
  reauth: (password: string, code?: string) =>
    request<{ ok: boolean; federated?: boolean; twoFactor?: boolean }>('/auth/reauth', {
      method: 'POST',
      body: JSON.stringify({ password, code }),
    }),

  /* ---- two-step sign-in ------------------------------------------------ */

  twoFactorStatus: () => request<TwoFactorStatus>('/auth/totp'),

  /**
   * Begin enrolling. Behind the same confirmation as any change to who can get
   * into the account, so this can raise the ConfirmIdentity dialog.
   */
  startTwoFactor: () =>
    request<{ secret: string; readable: string; uri: string }>('/auth/totp/start', {
      method: 'POST',
    }),

  /** Prove one code. The recovery codes come back here and nowhere else. */
  confirmTwoFactor: (code: string) =>
    request<{ ok: true; recoveryCodes: string[] }>('/auth/totp/confirm', {
      method: 'POST',
      body: JSON.stringify({ code }),
    }),

  /** Turn it off. Needs a current code — or a recovery code — as well. */
  disableTwoFactor: (code: string) =>
    request<{ ok: true }>('/auth/totp/disable', {
      method: 'POST',
      body: JSON.stringify({ code }),
    }),

  /**
   * Ask for a fresh confirmation link.
   *
   * Answers `ok` whether or not mail is configured here — there is no provider
   * in every deployment, and the token is logged server-side either way. The
   * UI must not promise an email arrived; it can only promise the request went
   * through.
   */
  requestEmailVerification: () => request<{ ok: true }>('/auth/verify/request', { method: 'POST' }),

  /**
   * Spend the token in the link that was emailed.
   *
   * Unauthenticated on purpose: the link may be opened in a browser that is
   * not signed in — a phone, a different profile — and the token is what
   * identifies the address, not the session.
   */
  confirmEmail: (token: string) =>
    request<{ ok: true }>('/auth/verify', { method: 'POST', body: JSON.stringify({ token }) }),

  /**
   * Ask to move the address this account signs in with.
   *
   * Nothing changes until the link sent to the NEW address is opened, so the
   * answer names where it went and the screen says so.
   */
  changeEmail: (email: string, password: string) =>
    request<{ ok: true; sentTo: string }>('/auth/email/change', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    }),

  /** Finish it. Every session ends, so the answer is followed by signing in. */
  confirmEmailChange: (token: string) =>
    request<{ ok: true; email: string }>('/auth/email/change/confirm', {
      method: 'POST',
      body: JSON.stringify({ token }),
    }),

  /* ---- content -------------------------------------------------------- */

  /**
   * `include: 'health'` adds the four Pulse counts from the SAME read of the
   * tree this call already makes.
   *
   * Opt-in because the assessment is the expensive half and every screen loads
   * this endpoint. Home asks for it; nothing else does. See the route.
   */
  state: (branch?: string, include?: 'health') =>
    request<State>(`/api/state${qs({ branch, include })}`),

  page: (path: string, branch?: string) =>
    request<{
      path: string
      content: string
      frontmatter: Frontmatter | null
      html: string | null
      /**
       * What this page was when it was read. Sent back on every save, so a write
       * against a page somebody else has changed since is refused rather than
       * replacing their work.
       */
      revision: string
      /** The block editor's document. Built server-side by the tested bridge. */
      doc: Record<string, unknown> | null
    }>(`/api/page${qs({ path, branch })}`),

  write: (
    branch: string,
    path: string,
    content: string,
    baseRevision?: string,
    /** This save accepts a model's suggestion, so the page is marked — §22. */
    aiAssisted?: boolean,
    /**
     * This save deliberately replaces somebody else's version.
     *
     * Only "keep mine" on the conflict screen sends it, and only the client can
     * know that: from the server's side the request is an ordinary save whose
     * base revision is current. The person whose work is being discarded is
     * told, which is the whole reason the field exists.
     */
    replacing?: string,
  ) =>
    request<{ path: string; canonicalised: boolean; revision: string }>('/api/page', {
      method: 'PUT',
      body: JSON.stringify({ branch, path, content, baseRevision, aiAssisted, replacing }),
    }),

  /**
   * Change some of a page's fields.
   *
   * `null` removes a field, and `undefined` does NOT: `JSON.stringify` drops a
   * key whose value is undefined, so it never reaches the server and the old
   * value survives. The type says so, so a caller cannot get it wrong quietly.
   */
  frontmatter: (branch: string, path: string, frontmatter: FrontmatterPatch) =>
    request<{ path: string }>('/api/frontmatter', {
      method: 'PUT',
      body: JSON.stringify({ branch, path, frontmatter }),
    }),

  /**
   * Save from the block editor. The document goes over the wire and the server
   * serialises it, so the browser never writes Markdown and the two surfaces
   * cannot produce different files.
   */
  writeDoc: (
    branch: string,
    path: string,
    doc: unknown,
    baseRevision?: string,
    /** This save accepts a model's suggestion, so the page is marked — §22. */
    aiAssisted?: boolean,
    /** See `write`: "keep mine" on the conflict screen, and nothing else. */
    replacing?: string,
  ) =>
    request<{ path: string; canonicalised: boolean; markdown: string; revision: string }>(
      '/api/page/doc',
      {
        method: 'PUT',
        body: JSON.stringify({ branch, path, doc, baseRevision, aiAssisted, replacing }),
      },
    ),

  /**
   * `branch` names whose variables the render resolves `{{name}}` with — the
   * draft on screen, so the preview shows what publishing it would show.
   */
  previewDoc: (doc: unknown, branch?: string) =>
    request<{ html: string; markdown: string }>('/api/preview/doc', {
      method: 'POST',
      // Same default as preview below: the draft's snippets and variables.
      body: JSON.stringify({ doc, branch: branch ?? currentDraft ?? undefined }),
    }),

  preview: (content: string, branch?: string) =>
    request<{ html: string }>('/api/preview', {
      method: 'POST',
      // The draft's tree by default — a snippet body staged a moment ago must
      // render in the node view that shows it.
      body: JSON.stringify({ content, branch: branch ?? currentDraft ?? undefined }),
    }),

  /* ---- variables -------------------------------------------------------- */

  /**
   * Every variable with its impact — the pages that reference it — plus the
   * names pages use that nothing defines, in one read. `revision` rides back
   * on the save, the same optimistic concurrency every page save carries.
   */
  variables: (branch?: string) =>
    request<{
      variables: VariableRow[]
      missing: { name: string; pages: string[] }[]
      revision: string
      /** The parse error, when variables.yaml itself is broken. */
      invalid: string | null
    }>(`/api/variables${qs({ branch })}`),

  /** Save the whole map onto the draft, through the normal staged-write path. */
  writeVariables: (branch: string, variables: Record<string, string>, baseRevision?: string) =>
    request<{ path: string; staged: boolean; revision: string }>('/api/variables', {
      method: 'PUT',
      body: JSON.stringify({ branch, variables, baseRevision }),
    }),

  diff: (branch: string) => request<{ diff: string }>(`/api/diff${qs({ branch })}`),

  move: (branch: string, from: string, to: string) =>
    request<{
      fromPath: string
      toPath: string
      /** Pages whose links were repointed. */
      relinked: string[]
      links: number
      /** Whether nav.yaml named the page and was updated. */
      nav: boolean
      /** Comment threads that moved with the page. */
      comments: number
      /** Pins and boosts in search.yaml that were repointed. */
      search: number
      /** Whether a site-level redirect in redirects.yaml had to follow the page. */
      redirectsUpdated: boolean
    }>('/api/move', { method: 'POST', body: JSON.stringify({ branch, from, to }) }),

  /**
   * Delete a page.
   *
   * Refused with `code: 'page_in_use'` and the list of pages that link to it,
   * unless `force` says the person has seen the list and meant it.
   */
  remove: (branch: string, path: string, force = false) =>
    request<{ path: string; brokeLinksOn: string[]; nav: boolean }>('/api/delete', {
      method: 'POST',
      body: JSON.stringify({ branch, path, force }),
    }),

  /* ---- the trash -------------------------------------------------------- */

  trash: () => request<{ pages: TrashedPage[] }>('/api/trash'),

  /**
   * Put a trashed page back onto a draft, as a staged change.
   *
   * Refused with `code: 'slug_taken'` and a suggested free address when the
   * page's own address is taken now — the server never overwrites, and only an
   * explicit `toPath` gets past the refusal.
   */
  restoreTrash: (id: string, branch: string, toPath?: string) =>
    request<{ path: string; originalPath: string; staged: boolean; placed: boolean }>(
      `/api/trash/${id}/restore`,
      {
        method: 'POST',
        body: JSON.stringify({ branch, ...(toPath ? { toPath } : {}) }),
      },
    ),

  /** Delete a page from the trash for good. */
  deleteTrash: (id: string) =>
    request<{ id: string; path: string }>(`/api/trash/${id}`, { method: 'DELETE' }),

  unstage: (branch: string, path: string) =>
    request<unknown>('/api/unstage', { method: 'POST', body: JSON.stringify({ branch, path }) }),

  /**
   * Start a draft.
   *
   * `version` names a frozen version this draft is FOR: it forks from that
   * version and publishes back into it, rather than onto the live
   * documentation. Absent means the live site, which is what a draft has
   * always meant.
   */
  createBranch: (name: string, version?: string) =>
    request<{ name: string; baseSha: string; targetRef: string | null }>('/api/branch', {
      method: 'POST',
      body: JSON.stringify({ name, ...(version ? { version } : {}) }),
    }),

  /**
   * Refused with `code: 'branch_moved'` and a whole `conflict` report when
   * somebody pushed to the repository while this draft was open. Nothing is
   * written by that refusal — see `resolveConflict` for what happens next.
   */
  commit: (branch: string, message: string) =>
    request<{ sha: string; paths: string[] }>('/api/commit', {
      method: 'POST',
      body: JSON.stringify({ branch, message }),
    }),

  /**
   * Put the writer's per-file decisions into effect.
   *
   * `head` is the sha the report was read against. Sent back deliberately: a
   * branch that moves a second time while somebody is reading a diff must not
   * have their now-stale decision applied to it, and the server answers that
   * with a fresh `branch_moved` rather than doing as it is told.
   *
   * Changes nothing in git. It drops the staged rows the writer gave up and
   * re-anchors the draft; committing afterwards is what writes.
   */
  resolveConflict: (branch: string, head: string, choices: Record<string, 'mine' | 'theirs'>) =>
    request<ConflictResolution>('/api/conflict/resolve', {
      method: 'POST',
      body: JSON.stringify({ branch, head, choices }),
    }),

  push: (branch: string) =>
    request<{ number: number; url: string }>('/api/push', {
      method: 'POST',
      body: JSON.stringify({ branch }),
    }),

  /* ---- assets ----------------------------------------------------------- */

  /**
   * Upload one file and get back the URL to reference it by.
   *
   * `FormData` rather than JSON, and no content-type header: the browser has to
   * set the multipart boundary itself, and naming a content-type here would
   * overwrite it with one that has no boundary at all.
   *
   * The server ignores whatever type the file claims and sniffs the bytes, so
   * this deliberately sends no type of its own — there is nothing useful to say.
   */
  uploadAsset: async (file: File, altText?: string) => {
    const form = new FormData()
    form.set('file', file)
    if (altText) form.set('altText', altText)

    const res = await fetch(apiUrl('/api/assets'), {
      method: 'POST',
      credentials: 'include',
      ...(workspaceSlug() ? { headers: { 'x-workspace': workspaceSlug()! } } : {}),
      body: form,
    })
    if (res.status === 401) throw new NotSignedIn()
    const body = (await res.json().catch(() => ({}))) as {
      asset?: {
        /** Given by the server, not assembled here: the route that serves it is
         *  the server's business, and rebuilding the path from the key would be
         *  a second copy of that decision. */
        url: string
        /** What `PATCH /api/theme` wants for a logo — the key, never the URL. */
        key: string
        altText: string | null
        contentType: string
        bytes: number
      }
      deduplicated?: boolean
      error?: string
    }
    if (!res.ok || !body.asset) throw new Error(body.error ?? `${res.status} ${res.statusText}`)
    return {
      /** What a page references. Content-addressed, so it never changes. */
      url: body.asset.url,
      key: body.asset.key,
      altText: body.asset.altText,
      contentType: body.asset.contentType,
      bytes: body.asset.bytes,
      /** True when these exact bytes were already here. Worth saying once. */
      deduplicated: Boolean(body.deduplicated),
    }
  },

  /** Every file in the workspace's library, newest first. */
  assets: () => request<{ assets: AssetRecord[] }>('/api/assets'),

  /**
   * What would break if this file were deleted.
   *
   * Asked for on demand rather than with every listing: it reads every open
   * draft as well as the published tree, so it is not free, and most files in a
   * library are never checked before somebody just looks at them.
   */
  assetUsage: (key: string) => request<AssetUsage>(`/api/assets/usage${qs({ key })}`),

  /** Set or correct alt text. Empty clears it. */
  setAssetAlt: (key: string, altText: string) =>
    request<{ ok: true }>('/api/assets/alt', {
      method: 'PUT',
      body: JSON.stringify({ key, altText }),
    }),

  /**
   * Delete a file.
   *
   * Refused with `code: 'in_use'` and the pages that reference it, unless
   * `force` says the person has seen that list and meant it anyway.
   */
  deleteAsset: (key: string, force = false) =>
    request<{ pages: string[] }>(`/api/assets${qs({ key, force: force ? '1' : undefined })}`, {
      method: 'DELETE',
    }),

  /**
   * Put a branch on the published site.
   *
   * The only irreversible step, and only a person can take it: the server
   * refuses anything that is not a human session, and refuses a person without
   * the publish grant.
   */
  /** Legacy all-main resolution, retained for older clients. */
  catchUp: (branch: string, takeLive: string[]) =>
    request<{ sha: string; baseSha: string; tookLive: string[] }>('/api/branch/catch-up', {
      method: 'POST',
      body: JSON.stringify({ branch, takeLive }),
    }),

  rebase: (branch: string, resolutions: Record<string, 'draft' | 'main'>) =>
    request<{ sha: string; baseSha: string; tookLive: string[]; staged: string[] }>(
      '/api/branch/rebase',
      {
        method: 'POST',
        body: JSON.stringify({ branch, resolutions }),
      },
    ),

  merge: (branch: string) =>
    request<{ sha: string; branch: string; previousSha: string; message: string }>('/api/merge', {
      method: 'POST',
      body: JSON.stringify({ branch }),
    }),

  publish: (message?: string) =>
    request<{ sha: string; branch: string; publication: string | null }>('/api/publish', {
      method: 'POST',
      body: JSON.stringify(message ? { message } : {}),
    }),

  /* ---- previews ----------------------------------------------------------- */

  previews: () => request<{ previews: PreviewLink[] }>('/api/previews'),

  /**
   * Share a branch. Returns the link once — see `PreviewLink`.
   *
   * The build is queued rather than run: the person is waiting for a link, and
   * the link exists already.
   */
  createPreview: (branch: string, days?: number) =>
    request<PreviewLink>('/api/previews', {
      method: 'POST',
      body: JSON.stringify({ branch, ...(days ? { days } : {}) }),
    }),

  revokePreview: (id: string) =>
    request<{ revoked: boolean }>(`/api/previews/${id}`, { method: 'DELETE' }),

  /**
   * What has gone out, newest first, with the state of the builds that make it
   * visible. One request, because between them they answer one question: is what
   * I published what readers can see?
   */
  publications: () =>
    request<{ publications: Publication[]; builds: SiteBuild[] }>('/api/publications'),

  /**
   * Put the site back to how it was before a publication.
   *
   * A new commit, never a rewrite. It answers to the same gates as publishing,
   * because it changes what readers see.
   */
  rollback: (id: string) =>
    request<{ sha: string; branch: string; publication: string | null }>(
      `/api/publications/${id}/rollback`,
      { method: 'POST' },
    ),

  /* ---- importing an archive ------------------------------------------------ */

  /**
   * Upload an export and queue the import of it.
   *
   * Answers as soon as the bytes are written down, with the job to watch —
   * converting a real documentation site takes minutes, and holding a request
   * open for them is how a closed laptop used to lose the whole run.
   *
   * Multipart rather than JSON because the body is a zip, and the fetch is
   * hand-rolled rather than through `request` because `request` sets a JSON
   * content type. The `x-workspace`/`x-project` headers still ride along —
   * importing into the wrong project would be the quiet disaster.
   */
  startImport: async (archive: File, platform?: string, version?: string): Promise<ImportJob> => {
    const form = new FormData()
    form.set('archive', archive)
    if (platform) form.set('platform', platform)
    // Absent means the live documentation. Given, the draft forks from that
    // version and publishes back into it.
    if (version) form.set('version', version)
    const res = await fetch(apiUrl('/api/import'), {
      method: 'POST',
      // Hand-rolled does not mean its own rules: without this the archive
      // arrives with no session at all once the API is on another host.
      credentials: 'include',
      headers: {
        ...(workspaceSlug() ? { 'x-workspace': workspaceSlug()! } : {}),
        ...(activeProject() ? { 'x-project': activeProject()! } : {}),
      },
      body: form,
    })
    if (res.status === 401) throw new NotSignedIn()
    const body = (await res.json().catch(() => ({}))) as { job?: ImportJob; error?: string }
    if (!res.ok || !body.job) {
      throw new Error(body.error ?? `${res.status} ${res.statusText}`)
    }
    return body.job
  },

  /** This project's imports, newest first. Polled while one is in flight. */
  importJobs: () => request<{ jobs: ImportJob[] }>('/api/import/jobs'),

  /**
   * Try a failed import again.
   *
   * It resumes from the last checkpoint rather than starting over, so retrying
   * a job that stopped on page 280 of 300 is twenty pages of work.
   */
  retryImport: (id: string) =>
    request<{ job: ImportJob }>(`/api/import/jobs/${id}/retry`, { method: 'POST' }),

  /**
   * The whole site as a zip: pages, nav, assets, and a manifest. Hand-rolled
   * for the same reason as `importArchive` — the response is bytes, not JSON —
   * and it returns the Blob plus the server's filename so the caller can hand
   * it to the browser as a download.
   */
  exportArchive: async (): Promise<{ blob: Blob; filename: string }> => {
    const res = await fetch(apiUrl('/api/export'), {
      // The same, and this one is a GET: a request with no body and no session
      // is answered as a stranger's, which for an export is a 401.
      credentials: 'include',
      headers: {
        ...(workspaceSlug() ? { 'x-workspace': workspaceSlug()! } : {}),
        ...(activeProject() ? { 'x-project': activeProject()! } : {}),
      },
    })
    if (res.status === 401) throw new NotSignedIn()
    if (!res.ok) {
      const body = (await res.json().catch(() => ({}))) as { error?: string }
      throw new Error(body.error ?? `${res.status} ${res.statusText}`)
    }
    const disposition = res.headers.get('content-disposition') ?? ''
    const match = /filename="([^"]+)"/.exec(disposition)
    return { blob: await res.blob(), filename: match?.[1] ?? 'export.zip' }
  },

  /** The same page rendered twice: the live site's words next to this draft's. */
  compare: (branch: string, path: string) =>
    request<{ before: string | null; after: string | null }>(
      `/api/compare?branch=${encodeURIComponent(branch)}&path=${encodeURIComponent(path)}`,
    ),

  /** New bytes take over every use of `key` on the draft, as staged edits. */
  replaceAsset: async (
    key: string,
    file: File,
    branch: string,
  ): Promise<{ asset: AssetRecord; pages: string[] }> => {
    const form = new FormData()
    form.set('file', file)
    form.set('key', key)
    form.set('branch', branch)
    const res = await fetch(apiUrl('/api/assets/replace'), {
      method: 'POST',
      credentials: 'include',
      headers: {
        ...(workspaceSlug() ? { 'x-workspace': workspaceSlug()! } : {}),
        ...(activeProject() ? { 'x-project': activeProject()! } : {}),
      },
      body: form,
    })
    if (res.status === 401) throw new NotSignedIn()
    const body = (await res.json().catch(() => ({}))) as {
      asset?: AssetRecord
      pages?: string[]
      error?: string
    }
    if (!res.ok || !body.asset) throw new Error(body.error ?? `${res.status} ${res.statusText}`)
    return { asset: body.asset, pages: body.pages ?? [] }
  },

  /** The tree's shared blocks, with the pages each one appears on. */
  snippets: (branch?: string) => {
    const wanted = branch ?? currentDraft
    return request<{
      snippets: { name: string; path: string; pages: string[] }[]
      missing: string[]
    }>(`/api/snippets${wanted ? `?branch=${encodeURIComponent(wanted)}` : ''}`)
  },

  /** Retire a snippet: detach (inline its body) or replace (repoint) first. */
  retireSnippet: (name: string, branch: string, mode: 'detach' | 'replace', replacement?: string) =>
    request<{ pages: string[] }>(`/api/snippets/${encodeURIComponent(name)}/retire`, {
      method: 'POST',
      body: JSON.stringify({ branch, mode, ...(replacement ? { replacement } : {}) }),
    }),

  /**
   * Delete the workspace. The name is typed back as the confirmation, the
   * server checks identity freshness (the retry layer raises the dialog),
   * and after a 200 the workspace stops resolving for everybody at once.
   */
  deleteWorkspace: (name: string) =>
    request<{ graceDays: number }>('/api/workspace', {
      method: 'DELETE',
      body: JSON.stringify({ name }),
    }),

  /** Delete the signed-in ACCOUNT; workspaces and their content stay. */
  deleteAccount: (email: string) =>
    request<{ ok: true }>('/auth/account', { method: 'DELETE', body: JSON.stringify({ email }) }),

  /* ---- comments --------------------------------------------------------- */

  comments: (path: string, branch?: string | null) =>
    request<{ comments: CommentThread[] }>(
      `/api/comments?path=${encodeURIComponent(path)}${branch ? `&branch=${encodeURIComponent(branch)}` : ''}`,
    ),
  commentCounts: (branch?: string | null) =>
    request<{ counts: Record<string, number> }>(
      `/api/comments/counts${branch ? `?branch=${encodeURIComponent(branch)}` : ''}`,
    ),
  addComment: (input: {
    path: string
    branch: string | null
    body: string
    quote?: string
    prefix?: string
    suffix?: string
    parentId?: string
  }) => request<CommentThread>('/api/comments', { method: 'POST', body: JSON.stringify(input) }),
  resolveComment: (id: string, resolved: boolean) =>
    request<{ ok: true }>(`/api/comments/${encodeURIComponent(id)}/resolve`, {
      method: 'POST',
      body: JSON.stringify({ resolved }),
    }),

  /* ---- documentation versions ------------------------------------------- */

  versions: () =>
    request<{
      versions: {
        id: string
        name: string
        label: string
        gitRef: string
        deprecated: boolean
        createdAt: string
      }[]
    }>('/api/versions'),
  cutVersion: (name: string, label?: string) =>
    request<{ name: string; label: string }>('/api/versions', {
      method: 'POST',
      body: JSON.stringify({ name, ...(label ? { label } : {}) }),
    }),
  setVersionDeprecated: (name: string, deprecated: boolean) =>
    request<{ ok: true }>(`/api/versions/${encodeURIComponent(name)}`, {
      method: 'PATCH',
      body: JSON.stringify({ deprecated }),
    }),
  retireVersion: (name: string) =>
    request<{ ok: true }>(`/api/versions/${encodeURIComponent(name)}`, { method: 'DELETE' }),

  notifications: () =>
    request<{ notifications: NotificationRecord[]; unread: number }>('/api/notifications'),
  markNotificationsRead: (id?: string) =>
    request<{ ok: true }>('/api/notifications/read', {
      method: 'POST',
      body: JSON.stringify(id ? { id } : {}),
    }),

  /* ---- the customer's own domain ---------------------------------------- */

  domainStatus: () => request<DomainStatus>('/api/domain'),
  claimDomain: (domain: string | null) =>
    request<{ state: string }>('/api/domain', {
      method: 'PUT',
      body: JSON.stringify({ domain }),
    }),
  /**
   * Returns the WHOLE status, and the screen shows that answer rather than
   * asking again.
   *
   * The address and the certificate are looked up while this runs — they are
   * the world's answer, not a stored one — so a re-fetch afterwards would show
   * a screen with the two newest things missing from it. Which is what it did:
   * press check, learn nothing, press check again.
   */
  verifyDomain: () => request<DomainStatus>('/api/domain/verify', { method: 'POST' }),

  /** The change-request conversation on a draft, and where it stands. */
  reviewThread: (branch: string) =>
    request<ReviewThread>(`/api/reviews/${encodeURIComponent(branch)}`),
  requestReview: (branch: string, note?: string, reviewerId?: string) =>
    request<ReviewThread>(`/api/reviews/${encodeURIComponent(branch)}/request`, {
      method: 'POST',
      body: JSON.stringify({ ...(note ? { note } : {}), ...(reviewerId ? { reviewerId } : {}) }),
    }),
  reviewComment: (branch: string, body: string) =>
    request<ReviewThread>(`/api/reviews/${encodeURIComponent(branch)}/comments`, {
      method: 'POST',
      body: JSON.stringify({ body }),
    }),
  /**
   * `sha` is the commit the reviewer's screen was drawn from, not the branch's
   * head at the moment of pressing — those differ exactly when somebody
   * committed while the reviewer was reading, which is the case this argument
   * exists to refuse. It comes from the `ReviewThread` currently on screen.
   */
  reviewVerdict: (
    branch: string,
    kind: 'approve' | 'request_changes',
    sha: string,
    note?: string,
  ) =>
    request<ReviewThread>(`/api/reviews/${encodeURIComponent(branch)}/verdict`, {
      method: 'POST',
      body: JSON.stringify({ kind, sha, ...(note ? { note } : {}) }),
    }),

  /** Substring search over the pages, honestly labelled: this is a grep. */
  search: (q: string, branch?: string) =>
    request<{ hits: SearchHit[] }>(`/api/search${qs({ q, branch })}`),

  /* ---- the projects in this workspace ------------------------------------ */

  /**
   * Every documentation site in this workspace, and which one is active.
   *
   * The route existed with nothing calling it, which meant a workspace could
   * hold several projects and a person could only ever see the first.
   */
  projects: () =>
    request<{
      projects: ProjectSummary[]
      active: string | null
      /**
       * Whether the plan includes another documentation site (§55). Optional so
       * an older deployment does not hide the control; absent means yes, which
       * is what every deployment did before plans were read.
       */
      mayAddAnother?: boolean
    }>('/api/projects'),

  /**
   * Remove a project, leaving its repository alone.
   *
   * The last one cannot go: a workspace without a project answers 409 to every
   * content request and has no way back through the interface.
   */
  removeProject: (id: string) =>
    request<{ removed: string; contentRepo: string | null }>(`/api/projects/${id}`, {
      method: 'DELETE',
    }),

  /** Add one. It gets its own repository, which is created before this returns. */
  createProject: (name: string) =>
    request<{ project: { id: string; slug: string; name: string } }>('/api/projects', {
      method: 'POST',
      body: JSON.stringify({ name }),
    }),

  /* ---- the guided first run ----------------------------------------------- */

  /** Where the first run stands, and what the docs are for. Any member may ask. */
  /**
   * The guided tours this person has already been shown, per PERSON — the
   * first run above is per workspace. See `createTours` in the core package.
   */
  tours: () => request<{ seen: string[]; waved: string[] }>('/api/tours'),
  /**
   * `finished` is the half that was thrown away. A tour dismissed by a stray
   * click on the dimming is not a tour somebody watched, and recording the two
   * the same way retired the guidance for ever — see migration 0111.
   */
  markTourSeen: (surface: string, options: { forget?: boolean; finished?: boolean } = {}) =>
    request<{ seen: string[]; waved: string[] }>('/api/tours', {
      method: 'POST',
      body: JSON.stringify({
        surface,
        ...(options.forget ? { forget: true } : {}),
        ...(options.finished === false ? { finished: false } : {}),
      }),
    }),

  onboarding: () => request<OnboardingStatus>('/api/onboarding'),

  /**
   * Record a first-run answer: the use case, that the run ended, or both.
   * `done` is idempotent on the server, so a second tab cannot re-onboard.
   */
  setOnboarding: (changes: { useCase?: UseCase; done?: true }) =>
    request<OnboardingStatus>('/api/onboarding', {
      method: 'POST',
      body: JSON.stringify(changes),
    }),

  /* ---- who is in this workspace ------------------------------------------ */

  workspaceSettings: () =>
    request<{
      slug: string
      name: string
      /**
       * What this plan includes, decided by the server's one table.
       *
       * The screen reads it to decide what to OFFER. It is not the check —
       * the route still refuses — but offering a paid control on Free and
       * refusing at Save is how a rule reads as a bug.
       */
      plan: {
        plan: string
        label: string
        customCss: boolean
        ourMark: boolean
        /** How many people may be here, invitations included. `null` is no limit. */
        collaborators: number | null
        /** How many documentation sites. `null` is no limit. */
        projects: number | null
      }
      /**
       * Every tier there is, cheapest first, for the plan cards.
       *
       * Sent by the server rather than written here, like `missedRule` and
       * `retentionDays`: `plans.ts` is the one table that decides what a plan
       * includes and what it costs, this bundle cannot import it, and a second
       * copy of the prices in a `.tsx` file is a copy that goes stale the day
       * one of them changes.
       */
      plans?: PlanTier[]
      /** `past_due` while Stripe retries a failed payment; see the Plan card. */
      billingStatus?: string
      /** Whether this deployment can take money; see the route. */
      billingConfigured?: boolean
      /** Whether YOUR address is confirmed. The checkout refuses one that is not. */
      emailConfirmed?: boolean
      /**
       * Whether signing in to this workspace needs a second step.
       *
       * A fact about the WORKSPACE rather than the site, so it sits with the
       * plan: a workspace's two documentation sites are not two front doors.
       */
      requireTwoFactor?: boolean
      /** Where this project's published site answers. Null when the deployment cannot say. */
      siteUrl: string | null
      domain: string | null
      contentRepo: string
      visibility: 'public' | 'password' | 'workspace'
      sitePasswordSet: boolean
      /** Where the OpenAPI file is. Null means we look in the usual places. */
      apiSpecPath: string | null
      /**
       * Whether this site answers readers' questions. Off until somebody says
       * so, because saying so is what sends passages to a model provider.
       */
      askEnabled: boolean
      /** Whether writers here may ask for help with the words — §22. */
      writingHelpEnabled: boolean
      /** Whether the questions readers ask are kept — §21. */
      askLogEnabled: boolean
      theme: {
        accent: string | null
        logoKey: string | null
        logoUrl: string | null
        /** The browser-tab icon. Null means a neutral one, never ours. */
        faviconKey: string | null
        faviconUrl: string | null
        /** The image a link to this site previews with. Null means none. */
        socialImageKey: string | null
        socialImageUrl: string | null
        /** A line above every page. Text and a link, never markup. */
        announcement: { text: string; href: string | null } | null
        /** Optional button in this site's published header. */
        cta: { label: string; href: string } | null
        /** Null until this project chooses; the site renders Standard meanwhile. */
        preset: string | null
        /** One of five code themes we ship. Null until this project chooses. */
        codeTheme: string | null
        /** Which type pairing this project chose. Null is the default. */
        fonts: string | null
        /** Their own CSS as they typed it, or null. Paid plans only. */
        customCss: string | null
      }
      project: { slug: string; name: string; repository: string | null } | null
      you: {
        role: Role
        canPublish: boolean
        canReview?: boolean
        canManageBilling?: boolean
        /**
         * The role in the WORKSPACE, before this project's override.
         *
         * `role` above says what this person may do in the documentation site they
         * are looking at; this says what they may do to the workspace — invite
         * somebody, make a project, read the audit trail. A project override is not
         * a promotion, and offering the workspace's own controls from it would offer
         * buttons the API refuses. Optional, because a deployment answering an older
         * shape should not blank the screen.
         */
        workspaceRole?: Role
      } | null
    }>('/api/workspace'),

  /**
   * Set — or clear — the active project's own accent colour and logo.
   *
   * `null` clears a field back to the default Staleless look; omitting it
   * leaves it alone — same distinction `updateWorkspace`'s domain makes.
   * Refused at the server with a message when the accent fails contrast or
   * the logo key was never uploaded to this project's own library.
   */
  /**
   * Hand the reading decision to the membership list.
   *
   * Its own call rather than a mode on `setSitePassword`, matching the routes:
   * one sets a shared secret, this says "the answer is who is in this
   * workspace". Called again, it rotates the key and signs every reader out,
   * which is how a site is closed again after somebody leaves.
   */
  setWorkspaceOnly: () => request<unknown>('/api/site-access/workspace', { method: 'PUT' }),

  /* ---- tokens a workspace mints for its own agents ---------------------- */

  tokens: () => request<{ tokens: ApiToken[]; scopes: string[]; maxDays: number }>('/api/tokens'),

  /**
   * The secret comes back ONCE, in this response, and is never stored where
   * the product can read it again.
   */
  mintToken: (input: { name: string; scopes: string[]; days: number }) =>
    request<{ token: string; record: ApiToken }>('/api/tokens', {
      method: 'POST',
      body: JSON.stringify(input),
    }),

  revokeToken: (id: string) =>
    request<{ revoked: boolean }>(`/api/tokens/${encodeURIComponent(id)}`, { method: 'DELETE' }),

  updateTheme: (changes: {
    accent?: string | null
    logoKey?: string | null
    faviconKey?: string | null
    socialImageKey?: string | null
    codeTheme?: string | null
    /** One of three type pairings, by name. See packages/schema's fonts.ts. */
    fonts?: string | null
    preset?: string | null
    /** Their own CSS. Scoped by the service; refused with a sentence. */
    customCss?: string | null
    ctaEnabled?: boolean
    ctaLabel?: string | null
    ctaHref?: string | null
  }) =>
    request<{
      theme: {
        accent: string | null
        logoKey: string | null
        logoUrl: string | null
        preset: string | null
        cta: { label: string; href: string } | null
      }
    }>('/api/theme', {
      method: 'PATCH',
      body: JSON.stringify(changes),
    }),

  /**
   * Put the ACTIVE project's published site behind a passphrase, or take it
   * out again. Null makes it public; the passphrase itself is never read back —
   * there is only a hash, so the interface offers set and replace, never show.
   */
  setSitePassword: (password: string | null) =>
    request<{ visibility: 'public' | 'password' | 'workspace'; sitePasswordSet: boolean }>(
      '/api/site-password',
      {
        method: 'PUT',
        body: JSON.stringify({ password }),
      },
    ),

  /**
   * Change the workspace's name or domain.
   *
   * `domain: null` clears it; omitting it leaves it alone. They are different
   * requests, and collapsing them would make a domain impossible to remove.
   */
  updateWorkspace: (changes: {
    name?: string
    domain?: string | null
    /** Whether signing in to this workspace needs a second step. */
    requireTwoFactor?: boolean
  }) =>
    request<{ slug: string; name: string; domain: string | null; requireTwoFactor?: boolean }>(
      '/api/workspace',
      {
        method: 'PATCH',
        body: JSON.stringify(changes),
      },
    ),

  /**
   * Hand the whole workspace to somebody else (§5).
   *
   * One act rather than two: they become the owner and the person pressing it
   * becomes an admin, in one transaction. Promoting a second owner is the other
   * control — `setMembership` below — and leaves both of them owners.
   */
  transferWorkspace: (userId: string) =>
    request<{ ok: true }>('/api/workspace/transfer', {
      method: 'POST',
      body: JSON.stringify({ userId }),
    }),

  setMembership: (
    userId: string,
    changes: { role?: Role; canPublish?: boolean; canReview?: boolean; canManageBilling?: boolean },
  ) =>
    request<{ ok: true }>(`/api/members/${userId}`, {
      method: 'PATCH',
      body: JSON.stringify(changes),
    }),

  members: () =>
    request<{
      members: Member[]
      invitations: Invitation[]
      /** Who has a different standing in THIS project. See migration 0067. */
      projectRoles: { userId: string; role: Role; canPublish: boolean | null }[]
      you: {
        role: Role
        canPublish: boolean
        canReview?: boolean
        canManageBilling?: boolean
        /**
         * The role in the WORKSPACE, before this project's override.
         *
         * `role` above says what this person may do in the documentation site they
         * are looking at; this says what they may do to the workspace — invite
         * somebody, make a project, read the audit trail. A project override is not
         * a promotion, and offering the workspace's own controls from it would offer
         * buttons the API refuses. Optional, because a deployment answering an older
         * shape should not blank the screen.
         */
        workspaceRole?: Role
      } | null
    }>('/api/members'),

  /**
   * A role in THIS project, overriding the workspace's.
   *
   * A workspace holds several sites and the people in it do not have the same
   * standing in each. `null` clears the override, so the workspace's answer
   * applies again.
   */
  setProjectRole: (userId: string, role: Role | null) =>
    role === null
      ? request<{ cleared: boolean }>(`/api/members/${encodeURIComponent(userId)}/project-role`, {
          method: 'DELETE',
        })
      : request<{ role: Role }>(`/api/members/${encodeURIComponent(userId)}/project-role`, {
          method: 'PUT',
          body: JSON.stringify({ role }),
        }),

  /**
   * Invite somebody, and get the link.
   *
   * The link comes back once and is stored nowhere, because only its hash is
   * kept. Losing it means inviting again, which supersedes the first — better
   * than a system that can hand the same capability out twice.
   */
  invite: (input: {
    email: string
    role: Role
    canPublish?: boolean
    canReview?: boolean
    canManageBilling?: boolean
  }) =>
    request<{ id: string; url: string; expiresAt: string; emailed: boolean }>(
      '/api/members/invite',
      {
        method: 'POST',
        body: JSON.stringify(input),
      },
    ),

  revokeInvitation: (id: string) =>
    request<{ ok: true }>(`/api/members/invite/${id}`, { method: 'DELETE' }),

  removeMember: (userId: string) =>
    request<{ ok: true }>(`/api/members/${userId}`, { method: 'DELETE' }),

  /**
   * The audit trail: every sensitive operation, who did it, and when.
   *
   * Owner or admin only — the server refuses anybody else, and this is the one
   * settings request `SettingsScreen` does not make at all for somebody who
   * cannot see it, rather than making it and rendering the refusal.
   *
   * Paged by `before`, a cursor rather than an offset: an offset shifts under
   * a page that is still receiving new rows, which an activity log always is.
   */
  auditLog: (before?: string) =>
    request<{ rows: AuditRow[]; nextBefore: string | null }>(`/api/audit${qs({ before })}`),

  /**
   * Open Stripe's own billing page: card, invoices, cancel (§56).
   *
   * Upgrading took two clicks and stopping took an email to support, which is
   * the asymmetry §56 calls a dark pattern. The URL is Stripe's; nothing about
   * the plan changes here, and a cancellation comes back as a signed webhook
   * like every other billing fact.
   */
  openBilling: () =>
    request<{ url: string }>('/api/billing/portal', { method: 'POST', body: '{}' }),

  /**
   * Start an upgrade, and get the address to send them to.
   *
   * The URL is Stripe's. Nothing here grants a plan: the product learns that
   * somebody paid from Stripe's signed webhook, not from a browser coming back.
   *
   * The plan is named because there are two to buy now. It is checked by the
   * route and again in `startCheckout` — a screen that picks the tier is not
   * the thing that decides the price.
   */
  startUpgrade: (plan: 'pro' | 'business' = 'pro', interval: 'month' | 'year' = 'month') =>
    request<{ url: string }>('/api/billing/checkout', {
      method: 'POST',
      body: JSON.stringify({ plan, interval }),
    }),

  /**
   * Start a workspace for an account that has none.
   *
   * On the auth routes, like accepting an invitation, and for the same reason:
   * the middleware that resolves a workspace refuses somebody who is in none,
   * so the one act that gives them one cannot live behind it.
   */
  createWorkspace: (name: string) =>
    request<{ workspace: Workspace; workspaces: Workspace[] }>('/auth/workspaces', {
      method: 'POST',
      body: JSON.stringify({ name }),
    }),

  /** Spend an invitation. On the auth routes: they are not a member yet. */
  acceptInvitation: (token: string) =>
    request<{ workspace: Workspace | null; workspaces: Workspace[] }>('/auth/invitation/accept', {
      method: 'POST',
      body: JSON.stringify({ token }),
    }),

  /* ---- webhooks --------------------------------------------------------- */

  /**
   * Every endpoint and the recent delivery log, in one request.
   *
   * Owner or admin only, on the READ as well as the writes — unusual, and
   * deliberate: a webhook URL routinely carries a token in its path, so the
   * list of endpoints is a list of credentials. The screen makes this request
   * only for somebody who can see it, the same way it treats the audit log.
   */
  webhooks: () =>
    request<{
      endpoints: WebhookEndpoint[]
      deliveries: WebhookDelivery[]
      disableAfter: number
      maxAttempts: number
      events: string[]
    }>('/api/webhooks'),

  /**
   * Add one, and receive the signing secret.
   *
   * `secret` is on THIS reply and no other. There is no route that reads one
   * back, so a UI that shows it and expects to show it again later is a UI
   * with a copy button that quietly stops working — the same property the
   * preview link and the recovery codes have.
   */
  addWebhook: (url: string) =>
    request<{ endpoint: WebhookEndpoint; secret: string }>('/api/webhooks', {
      method: 'POST',
      body: JSON.stringify({ url }),
    }),

  setWebhookEnabled: (id: string, enabled: boolean) =>
    request<{ endpoint: WebhookEndpoint }>(`/api/webhooks/${id}`, {
      method: 'PATCH',
      body: JSON.stringify({ enabled }),
    }),

  removeWebhook: (id: string) => request<{ ok: true }>(`/api/webhooks/${id}`, { method: 'DELETE' }),

  /** Queue a test event. It is delivered by the same worker as a real one. */
  testWebhook: (id: string) =>
    request<{ delivery: WebhookDelivery }>(`/api/webhooks/${id}/test`, { method: 'POST' }),

  /* ---- the inbox -------------------------------------------------------- */

  /**
   * What needs attention.
   *
   * `open` by default. Anything else has to be asked for, because an inbox that
   * shows everything ever dismissed is a log, not an inbox.
   */
  inbox: (states?: SuggestionState[]) =>
    request<{ suggestions: Suggestion[]; counts: Record<string, number> }>(
      `/api/inbox${qs({ state: states?.join(',') })}`,
    ),

  /**
   * Re-assess the workspace and update the inbox.
   *
   * Reads every page and asks git for each one's age, so it is slow by nature —
   * seconds, not milliseconds. Nothing should wait on it.
   */
  scan: (branch?: string) =>
    request<{
      counts: { opened: number; reopened: number; unchanged: number; resolved: number }
      summary: { total: number; current: number; review: number; outdated: number; score: number }
      pages: number
      withoutDates: boolean
    }>(`/api/scan${qs({ branch })}`, { method: 'POST' }),

  /**
   * Pulse's own assessment, read only.
   *
   * The same freshness assessment `scan` produces, without writing anything to
   * the inbox — reading how healthy the documentation is must not change what
   * the shared inbox says. A viewer may call this even though `scan` refuses
   * them.
   */
  health: (branch?: string) =>
    request<{ summary: PulseSummary; pages: PageHealth[]; withoutDates: boolean }>(
      `/api/health${qs({ branch })}`,
    ),

  /**
   * Be quiet about this page for a while.
   *
   * Silences the notice and nothing else: the row still says the page is
   * overdue, because a snooze is a decision about being interrupted rather
   * than a claim about the page.
   */
  /**
   * Set — or remove — the line above every page.
   *
   * `null` removes it. An empty string is refused rather than treated as a
   * removal: "I saved a blank banner" and "I removed the banner" are
   * different intentions.
   */
  setAnnouncement: (announcement: { text: string; href?: string | null } | null) =>
    request<unknown>('/api/announcement', {
      method: 'PATCH',
      body: JSON.stringify({ announcement }),
    }),

  /**
   * Write the OpenAPI file out as documentation pages, onto a draft.
   *
   * A page per endpoint, grouped by tag. Pages somebody has edited are kept
   * and named in the report rather than overwritten — the sentence a person
   * added to explain what an endpoint is really for is the whole value they
   * add to a generated reference.
   */
  generateApiReference: () =>
    request<{
      branch: string
      source: string
      written: string[]
      kept: string[]
      unchanged: string[]
      placed: string[]
      groups: string[]
      notes: string[]
    }>('/api/api-reference', { method: 'POST' }),

  /**
   * Start from a template: the pages every site of this kind needs.
   *
   * Onto a draft, and never over a page that already exists. The use case is
   * the one the first run asked about; naming it here is for the screen that
   * asks and presses in the same breath.
   */
  startFromTemplate: (useCase?: 'product' | 'api' | 'help' | 'handbook') =>
    request<{
      branch: string
      useCase: string
      label: string
      written: string[]
      kept: string[]
      /** Pages added to nav.yaml, so the draft can be committed. */
      placed: string[]
    }>('/api/template', {
      method: 'POST',
      body: JSON.stringify(useCase ? { useCase } : {}),
    }),

  /**
   * Write a changelog draft from what has actually been published.
   *
   * The publishes are already recorded; a reader asks what changed more often
   * than a writer remembers to say. Between markers, so a hand-written
   * introduction survives the next regeneration.
   */
  writeChangelog: () =>
    request<{ branch: string; entries: number; latest: string | null; kept: boolean }>(
      '/api/changelog',
      { method: 'POST' },
    ),

  /**
   * What this project's API description resolves to, and where we looked.
   *
   * The setting alone does not answer that: most projects never name a file,
   * because the engine finds one in the usual places.
   */
  apiSpec: () =>
    request<{
      configured: string | null
      resolved: string | null
      /** What is wrong with the file, and where — see `openApiFindings`. */
      findings: { at: string; says: string; severity: 'error' | 'note' }[]
      usualPlaces: string[]
    }>('/api/api-spec'),

  /**
   * Say where this project's OpenAPI file lives, or null to look around.
   *
   * The setting that makes the API-change inbox possible: without a
   * specification there is nothing to compare, and a customer had no way to
   * point at one.
   */
  setApiSpecPath: (path: string | null) =>
    request<{ apiSpecPath: string | null }>('/api/api-spec', {
      method: 'PATCH',
      body: JSON.stringify({ path }),
    }),

  /**
   * Let this site answer readers' questions, or stop it.
   *
   * The one control in the product that starts sending a customer's content to
   * a model provider, which is why it is owner-or-admin and why docs/PRIVACY.md
   * §4 describes it rather than merely listing it.
   */
  setAskEnabled: (enabled: boolean) =>
    request<{ askEnabled: boolean }>('/api/ask', {
      method: 'PATCH',
      body: JSON.stringify({ enabled }),
    }),

  /**
   * A suggestion for the selected text. Writes nothing: what comes back is
   * shown beside the original and applied only if the writer says so.
   */
  writingSuggestion: (action: string, text: string, branch?: string, language?: string) =>
    request<{ action: string; before: string; after: string; unchanged: boolean }>('/api/writing', {
      method: 'POST',
      body: JSON.stringify({
        action,
        text,
        ...(branch ? { branch } : {}),
        // Only `translate` sends one, and only one of the twelve the control offers.
        ...(language ? { language } : {}),
      }),
    }),

  /**
   * What readers asked, and whether the documentation answered — §21.
   *
   * Its own request rather than part of the analytics summary: a project that
   * does not keep questions has nothing here, and every Analytics load would
   * pay for an empty list.
   */
  askedQuestions: () =>
    request<{
      keeping: boolean
      asked: number
      answered: number
      days: number
      rated: number
      helpful: number
      questions: {
        question: string
        asked: number
        answered: number
        isGap: boolean
        /** Answered, and readers said it did not help: worse than a gap. */
        isWrong: boolean
        helpful: number
        notHelpful: number
        cited: string[]
        followed: string[]
        lastAsked: string
      }[]
    }>('/api/ask/questions'),

  /**
   * Draft the change a finding asks for — §35.
   *
   * Writes nothing: the answer is the page as it is and the page as it could
   * be, and a person decides. Needs help with the words to be on, because it is
   * the same provider seeing the same content.
   */
  draftFromFinding: (id: string, path: string, branch?: string) =>
    request<{ path: string; before: string; after: string; unchanged: boolean }>(
      `/api/inbox/${id}/draft`,
      { method: 'POST', body: JSON.stringify({ path, ...(branch ? { branch } : {}) }) },
    ),

  /**
   * What the AI features have cost this project today — §55.
   *
   * Read from the same counters the routes enforce, so the screen cannot drift
   * from the rule. `configured: false` means this deployment has no provider and
   * nothing can be spent at all.
   */
  aiUsage: () =>
    request<{
      configured: boolean
      /** Whose numbers these are: the allowance comes with the plan (§55). */
      plan: string
      /** What an owner set for this site, or null for "what the plan includes". */
      cap: number | null
      writing: {
        on: boolean
        you: { used: number; limit: number }
        project: { used: number; limit: number }
        resetsInSeconds: number
      }
      ask: { on: boolean; site: { used: number; limit: number }; resetsInSeconds: number }
    }>('/api/ai-usage'),

  /**
   * The ceiling for this site's model calls, below what the plan includes.
   *
   * `null` clears it. A number above the plan is stored and does nothing: the
   * routes take the smaller of the two, which is what makes this a floor an
   * owner puts under their allowance rather than a way to buy more.
   */
  setAiCap: (cap: number | null) =>
    request<{ cap: number | null }>('/api/ai-cap', {
      method: 'PATCH',
      body: JSON.stringify({ cap }),
    }),

  /** Keep the questions readers ask, or stop keeping them. */
  setAskLogEnabled: (enabled: boolean) =>
    request<{ askLogEnabled: boolean }>('/api/ask/log', {
      method: 'PATCH',
      body: JSON.stringify({ enabled }),
    }),

  /** Let writers in this project ask for that help, or stop them. */
  setWritingHelpEnabled: (enabled: boolean) =>
    request<{ writingHelpEnabled: boolean }>('/api/writing-help', {
      method: 'PATCH',
      body: JSON.stringify({ enabled }),
    }),

  snoozeReview: (path: string, days: number) =>
    request<{ path: string; snoozedUntil: string }>('/api/review/snooze', {
      method: 'POST',
      body: JSON.stringify({ path, days }),
    }),

  wakeReview: (path: string) =>
    request<{ path: string; snoozedUntil: null }>('/api/review/wake', {
      method: 'POST',
      body: JSON.stringify({ path }),
    }),

  /**
   * The site's own redirects, and where each one lands.
   *
   * `lands` is the point: a redirect pointing at a page nobody wrote is the
   * most common mistake in a migration, and it is invisible in a YAML file.
   */
  redirects: (branch?: string) =>
    request<{ redirects: RedirectRow[]; revision: string; invalid: string | null }>(
      `/api/redirects${qs({ branch })}`,
    ),

  writeRedirects: (branch: string, redirects: RedirectRow[], baseRevision?: string) =>
    request<{ revision: string }>('/api/redirects', {
      method: 'PUT',
      body: JSON.stringify({
        branch,
        redirects: redirects.map((r) => ({ from: r.from, to: r.to, status: r.status })),
        ...(baseRevision === undefined ? {} : { baseRevision }),
      }),
    }),

  /**
   * Remove the `redirect_from` entries that cannot work.
   *
   * A loop and a conflict both BLOCK a publish, and the repair for both is one
   * call — which had no door until now, so the way past a bad import was to
   * open every page and edit YAML. Staged on the draft like any other edit, so
   * it appears in the diff rather than happening behind the writer's back.
   */
  tidyRedirects: (branch: string) =>
    request<{ changed: string[]; collapsed: { path: string; url: string; reason: string }[] }>(
      '/api/redirects/tidy',
      { method: 'POST', body: JSON.stringify({ branch }) },
    ),

  /** What readers said, per published page, worst pages first. Read only. */
  feedback: () => request<{ pages: ReaderFeedbackPage[] }>('/api/feedback'),

  /** What readers searched for, gaps first. Read only. */
  searchAnalytics: () =>
    request<{ queries: SearchQueryStat[]; questions: SearchQuestionStat[] }>('/api/search-events'),

  /**
   * How much of the published site is read, for one window.
   *
   * `days` must be a window the product offers — 1, 7, 30 or 90 — and the
   * server refuses anything else rather than quietly answering a different
   * question than the screen's label claims.
   */
  analytics: (days: number) =>
    request<AnalyticsSummary>(`/api/analytics${qs({ days: String(days) })}`),

  /** Change how long this project keeps its reader analytics. */
  setAnalyticsRetention: (days: number) =>
    request<{ days: number }>('/api/analytics/retention', {
      method: 'POST',
      body: JSON.stringify({ days }),
    }),

  actOnSuggestion: (id: string, action: SuggestionAction, until?: Date) =>
    request<{ state: SuggestionState }>(`/api/inbox/${id}`, {
      method: 'POST',
      body: JSON.stringify({ action, ...(until ? { until: until.toISOString() } : {}) }),
    }),

  /**
   * A page's revisions. `source` is where each one came from — `editor`, `api`,
   * `import`, `rollback`, or `git` for a commit pushed straight to the
   * repository. See packages/git/src/source.ts.
   */
  log: (branch?: string, path?: string) =>
    request<{
      commits: {
        sha: string
        message: string
        author: string
        date: string
        source: 'editor' | 'api' | 'import' | 'rollback' | 'git'
      }[]
    }>(`/api/log${qs({ branch, path })}`),

  /**
   * Where this project's documentation lives, when the customer owns it.
   *
   * Readable by anybody in the project: which repository a site reads is not a
   * secret from the people writing it, and somebody who can see "reconnect
   * needed" can tell an admin.
   */
  githubConnection: () => request<GitHubConnection>('/api/github/connection'),

  /** The repositories the installation can see, for the picker. */
  githubRepositories: () =>
    request<{ repositories: GitHubRepository[]; more: boolean }>('/api/github/repositories'),

  githubBranches: (repo: string) =>
    request<{ branches: string[] }>(`/api/github/branches${qs({ repo })}`),

  /** Point this project at a repository, a branch and a directory inside it. */
  connectRepository: (repo: string, branch: string, path: string) =>
    request<{ connection: GitHubConnection['connection'] }>('/api/github/connection', {
      method: 'PUT',
      body: JSON.stringify({ repo, branch, path }),
    }),

  /** Ask GitHub now, and record what it answered. The reconnect button. */
  checkGitHubConnection: () =>
    request<{ connection: GitHubConnection['connection'] }>('/api/github/connection/check', {
      method: 'POST',
    }),

  /** Stop reading it, and put the project back where it was. */
  disconnectRepository: () =>
    request<{ disconnected: boolean }>('/api/github/connection', { method: 'DELETE' }),

  /** Which Slack workspaces search this site, and where to add another. */
  slackConnection: () => request<SlackConnection>('/api/slack/connection'),

  /** Where this installation's stale alerts go. Null stops them. */
  setSlackAlertChannel: (team: string, channel: string | null) =>
    request<{ installation: SlackInstallation }>(
      `/api/slack/connection/${encodeURIComponent(team)}/alerts`,
      { method: 'PUT', body: JSON.stringify({ channel }) },
    ),

  /** Forget the token. Removing the app in Slack is still theirs to do. */
  disconnectSlack: (team: string) =>
    request<{ disconnected: boolean }>(`/api/slack/connection/${encodeURIComponent(team)}`, {
      method: 'DELETE',
    }),

  /* ---- the seven a customer brings themselves -------------------------- */

  /**
   * Which helpdesks and trackers this deployment can offer, and what is
   * connected here.
   *
   * Readable by anybody in the project, the same reasoning as the repository
   * and Slack cards beside it: which helpdesk feeds this site's suggestions is
   * not a secret from the people writing the pages.
   */
  integrations: () => request<IntegrationsView>('/api/integrations'),

  /**
   * Mint the link that starts a trip to a vendor.
   *
   * A POST rather than a link on the list, because the link carries a signed
   * fifteen-minute state and there is no reason to hand one to a viewer the
   * next step will refuse — and because Zendesk's address contains a subdomain
   * only the customer knows.
   */
  startIntegration: (vendor: string, host?: string) =>
    request<{ url: string }>(`/api/integrations/${encodeURIComponent(vendor)}/start`, {
      method: 'POST',
      body: JSON.stringify(host ? { host } : {}),
    }),

  /** Trello's second half: the token its return address left in the fragment. */
  connectTrello: (state: string, token: string) =>
    request<{ connection: IntegrationConnection }>('/api/integrations/trello/token', {
      method: 'POST',
      body: JSON.stringify({ state, token }),
    }),

  /** What this connection points at — a Trello board, and nothing secret. */
  setIntegrationSettings: (
    vendor: string,
    account: string,
    settings: Record<string, string | null>,
  ) =>
    request<{ connection: IntegrationConnection }>(
      `/api/integrations/${encodeURIComponent(vendor)}/${encodeURIComponent(account)}/settings`,
      { method: 'PUT', body: JSON.stringify({ settings }) },
    ),

  /** Ask the vendor now, and record what it said. The reconnect button. */
  checkIntegration: (vendor: string, account: string) =>
    request<{ connection: IntegrationConnection }>(
      `/api/integrations/${encodeURIComponent(vendor)}/${encodeURIComponent(account)}/check`,
      { method: 'POST' },
    ),

  /** Forget the credential. Removing the app at the vendor is still theirs. */
  disconnectIntegration: (vendor: string, account: string) =>
    request<{ disconnected: boolean; leaves: string }>(
      `/api/integrations/${encodeURIComponent(vendor)}/${encodeURIComponent(account)}`,
      { method: 'DELETE' },
    ),
}

/** One non-secret thing a customer fills in after connecting. Trello's board. */
export interface IntegrationSetting {
  key: string
  label: string
  note: string
  placeholder: string
  /** The integration reads nothing until this is given. */
  required: boolean
}

/** One connected helpdesk or tracker. Never a credential. */
export interface IntegrationConnection {
  account: string
  accountName: string | null
  scopes: string[]
  settings: Record<string, unknown>
  connectedName: string
  connectedAt: string
  /** When it last answered a read. Null for one that never has. */
  workedAt: string | null
  lastError: string | null
  erroredAt: string | null
  expiresAt: string | null
  /**
   * Why this connection cannot read anything yet, in a sentence. Null when it
   * can.
   *
   * Sent by the server rather than worked out here, because the answer depends
   * on what the vendor granted and on settings only it knows the shape of —
   * and the alternative is a card that looks healthy above an integration that
   * reads nothing.
   */
  problem: string | null
}

/** One of the seven, whether or not this deployment can offer it. */
export interface IntegrationCard {
  vendor: string
  label: string
  kind: 'support' | 'issue'
  buys: string
  /** What disconnecting does NOT do. Said before they press, and after. */
  leaves: string
  /** False when this deployment has no app with that vendor. */
  configured: boolean
  /** Which variables are unset, when it is not. Names, never values. */
  missing: string[]
  /** The customer's own hostname, asked for before the trip starts. */
  host: { label: string; note: string; suffix: string } | null
  scopes: string[]
  /** Where the read-only choice is made, for a vendor that takes no scopes. */
  permissions: string | null
  settings: IntegrationSetting[]
  connection: IntegrationConnection | null
}

export interface IntegrationsView {
  integrations: IntegrationCard[]
}

/** One Slack workspace that has this documentation site connected. */
export interface SlackInstallation {
  teamId: string
  teamName: string | null
  scopes: string[]
  alertChannel: string | null
  /**
   * Why alerts cannot be posted, in a sentence. Null when they can.
   *
   * Sent by the server rather than worked out here, because the answer depends
   * on which permissions Slack GRANTED when the app was added — a fact this
   * screen has no other way to know, and one that would otherwise arrive as a
   * `missing_scope` from an API the reader has never heard of.
   */
  alertProblem: string | null
  installedName: string
  createdAt: string
}

/**
 * The Slack card's whole state.
 *
 * `configured` is about the DEPLOYMENT — an installation of Staleless with no
 * Slack app offers no button at all — and `installations` is about this
 * project. `unfurlDomains` is the one thing the card must say out loud rather
 * than let somebody discover: link previews only ever work on the domains the
 * Slack app itself declares.
 */
export interface SlackConnection {
  configured: boolean
  installUrl: string | null
  installations: SlackInstallation[]
  unfurlDomains: string[]
}

/** One repository the customer's installation can see. */
export interface GitHubRepository {
  fullName: string
  defaultBranch: string
  private: boolean
}

/**
 * The connection screen's whole state — §31.
 *
 * `configured` is about the DEPLOYMENT and `connection` is about this project,
 * and the screen says something different for each: a deployment with no App
 * offers no button at all, while a project with no connection offers the one
 * that starts the trip to GitHub.
 */
export interface GitHubConnection {
  configured: boolean
  /** Whether a pull request preview can be linked to. See `previewBase`. */
  previewChecks: boolean
  connection: {
    state: 'connected' | 'needs_reconnect'
    account: string
    repo: string | null
    branch: string | null
    path: string
    lastSyncAt: string | null
    lastSyncSha: string | null
    lastError: string | null
    lastErrorAt: string | null
  } | null
  installUrl: string | null
}

/**
 * A workspace's OWN identity provider, and the domains it speaks for.
 *
 * The settings half. The sign-in half is `ssoStart` below, which the gate
 * calls before it asks for a password: a person who works somewhere with a
 * directory should meet their own directory rather than a password box.
 */
export const sso = {
  /** The directory, the domains, and what to paste into the provider. */
  settings: () => request<SsoSettings>('/api/sso'),

  /** Turn a pasted federation metadata document into the three fields. */
  readMetadata: (metadata: string) =>
    request<{ entityId: string; ssoUrl: string; certificates: string[] }>('/api/sso/metadata', {
      method: 'POST',
      body: JSON.stringify({ metadata }),
    }),

  /** Save it. Reconnecting keeps the mapping, the role and the domains. */
  configure: (configuration: SsoConfiguration) =>
    request<SsoSettings & { warning?: string }>('/api/sso', {
      method: 'PUT',
      body: JSON.stringify(configuration),
    }),

  /** Stop signing in through a directory. The proved domains stay. */
  disconnect: () => request<SsoSettings>('/api/sso', { method: 'DELETE' }),

  /** Claim a domain, which issues the TXT record to publish. */
  addDomain: (domain: string) =>
    request<SsoSettings>('/api/sso/domains', {
      method: 'POST',
      body: JSON.stringify({ domain }),
    }),

  /** Look for the record. Says which of the two DNS failures happened. */
  verifyDomain: (domain: string) =>
    request<SsoSettings>(`/api/sso/domains/${encodeURIComponent(domain)}/verify`, {
      method: 'POST',
    }),

  /** Give it up. Nobody at that domain is routed here afterwards. */
  removeDomain: (domain: string) =>
    request<SsoSettings>(`/api/sso/domains/${encodeURIComponent(domain)}`, { method: 'DELETE' }),
}

/**
 * "Does this address belong to an organisation with its own directory?"
 *
 * Asked from the sign-in screen before a password is offered. `sso: false` is
 * the ordinary answer and means "carry on with the password". It says nothing
 * about whether an ACCOUNT exists at that address — the question is about the
 * DOMAIN, and which people at a company have accounts is not a thing an
 * unauthenticated caller may learn.
 */
export const ssoStart = (email: string) =>
  request<{ sso: boolean; label?: string; url?: string }>('/auth/sso/start', {
    method: 'POST',
    body: JSON.stringify({ email }),
  })

/** How a workspace's directory is configured. Never carries the client secret back. */
export interface SsoConfiguration {
  protocol: 'oidc' | 'saml'
  label?: string
  issuer: string
  clientId?: string
  clientSecret?: string
  ssoUrl?: string
  certificates?: string[]
  mapping?: {
    email?: string
    name?: string
    firstName?: string
    lastName?: string
    subject?: string
  }
  defaultRole?: 'admin' | 'editor' | 'viewer'
}

/** The settings screen's whole state. */
export interface SsoSettings {
  /** False when this deployment has no key to encrypt a client secret with. */
  configurable: boolean
  /**
   * What to paste into the directory console — always, and before anything is
   * saved, because every console asks for these while the application is being
   * created. See the server, which builds all three from one function.
   */
  /**
   * Optional only across a rolling deploy: servers before 2026-08-31 returned
   * these addresses only after a connection existed. Current servers always
   * provide them.
   */
  addresses?: { callbackUrl: string; entityId: string; acsUrl: string }
  connection: {
    protocol: 'oidc' | 'saml'
    label: string
    issuer: string
    clientId: string | null
    ssoUrl: string | null
    certificates: string[]
    defaultRole: 'admin' | 'editor' | 'viewer'
    problem: string | null
    problemAt: string | null
    updatedAt: string
    /** What the customer pastes into their own directory's console. */
    entityId: string
    acsUrl: string
    metadataUrl: string
  } | null
  domains: {
    domain: string
    state: 'pending' | 'verified'
    /** The record to publish. Null once the domain is proved. */
    record: { name: string; type: 'TXT'; value: string } | null
    problem: string | null
    checkedAt: string | null
    verifiedAt: string | null
    claimedName: string
  }[]
}
